/* =====================================================================
 * pptxParser.ts — OOXML(PPTX) → 내부 모델 파서
 * JSZip + DOMParser 기반으로 presentation.xml, slideN.xml, theme1.xml,
 * slideMasters, slideLayouts, media/*, _rels 를 파싱
 * ===================================================================== */

import JSZip from 'jszip';

import { emfToSvg } from './emfToSvg';
import { parseChartPart } from './pptxChartParser';
import type { PptxChart } from './pptxChartParser';

export type { PptxChart, PptxChartSeries } from './pptxChartParser';

/* ------------------------------------------------------------------ */
/* Types — 내부 프레젠테이션 모델                                         */
/* ------------------------------------------------------------------ */

export interface PptxPresentation {
  slides: PptxSlide[];
  slideSize: { width: number; height: number };
  theme: PptxTheme;
  slideMasters: PptxSlideMaster[];
  slideLayouts: PptxSlideLayout[];
  media: Map<string, string>;
  rawParts: Map<string, string>;
  zip: JSZip;
  /** 문서 전체에서 실제 텍스트 런에 사용된 폰트 이름 목록 */
  usedFonts: string[];
  /** 편집기가 슬라이드 목록(추가/복제/삭제/이동)을 바꿨을 때만 true — 패처가
   *  sldIdLst/rels/[Content_Types] 재조정(reconcilePresentation)을 이 플래그로 게이트. */
  slideListDirty?: boolean;
  /** 편집기가 삭제한 슬라이드의 partPath 목록(명시적 삭제 집합 — deletedCNvIds 와
   *  동일 원리). 패처는 "모델에 없음"이 아니라 이 집합으로만 파트를 지워,
   *  undo 로 복귀한 슬라이드를 오삭제하지 않는다(모델에 있으면 삭제 스킵). */
  deletedSlideParts?: string[];
  /** 저장에서 제거한 슬라이드의 복원 자료. undo는 모델만 복구하므로 원본 파트와
   *  패키지 관계를 세션 동안 보관한다. 슬라이드별 undo 스냅샷에는 복제하지 않는다. */
  slideArchiveSnapshots?: Map<string, PptxSlideArchiveSnapshot>;
  /** 파스 시점 sldIdLst 의 슬라이드 partPath 목록(파스 실패로 모델에 못 담은 파트
   *  포함). 패처의 sldIdLst 재작성 시 "모델에 없는 sldId" 중 이 목록에 있는 것만
   *  보존한다 — 이전 저장이 만든 뒤 undo 로 모델에서 사라진 고스트 파트를
   *  파스실패 슬라이드와 구별해 완전 제거하기 위한 기준선. */
  sldIdLstParts?: string[];
  /** 편집기가 삽입한 새 이미지의 원본(imageRId → dataURL/확장자). 슬라이드
   *  스냅샷(undo 딥클론) 밖에 둬 대용량 base64 복제를 피한다 — 패처가 저장 시
   *  media 파트/rel 로 구워쓴다(파서는 설정하지 않음). */
  newImageMedia?: Map<string, { dataUrl: string; ext: string }>;
  /** 세션 중 발급한 최대 cNvId(단조, undo 스냅샷 밖 — 파서는 설정하지 않음).
   *  삽입→삭제/undo 뒤 재발급 시 같은 cNvId(→같은 이미지 rId)가 재사용돼
   *  newImageMedia 바이트가 다른 이미지로 덮이는 무음 손상을 막는 카운터. */
  sessionMaxCNvId?: number;
}

export interface PptxSlideArchiveSnapshot {
  parts: Array<{ path: string; xml: string; rawXml?: string; contentTypeXml?: string }>;
  slideIdXml: string;
  relationshipXml: string;
  source: NonNullable<PptxSlide['duplicateSourceSnapshot']>;
}

export interface PptxSlide {
  id: string;
  index: number;
  partPath: string;
  shapes: PptxShape[];
  background?: PptxBackground;
  layoutRef?: string;
  colorMap?: PptxColorMap;
  dirty?: boolean;
  /**
   * 편집기가 도형을 추가/삭제/복제/재정렬(구조편집)했을 때만 true. 패처는 이
   * 슬라이드에 한해 spTree 를 모델 도형 리스트(완전집합·순서)에 맞춰 재구성한다
   * (reconcileShapeTree). false 인 슬라이드는 종전 in-place 패치(patchShapeTree,
   * 미매칭 요소 보존)로 남겨 무구조편집 슬라이드의 회귀 위험을 격리한다.
   */
  structuralDirty?: boolean;
  /**
   * 파트 전체 cNvPr@id 최대값(파서가 모델에 안 담은 요소·root nvGrpSpPr 포함).
   * 편집기가 신규 도형 cNvId 를 발급할 때 이 값과 모델 최대값의 max+1 을 써서
   * 파서 드롭 요소(SmartArt/OLE 등)와의 id 충돌을 막는다.
   */
  maxCNvId?: number;
  /**
   * 편집기가 삭제한 도형의 cNvId 목록. reconcileShapeTree 는 "모델에 없음"이 아니라
   * 이 집합으로 삭제를 판정한다 — 파서가 모델에 안 담은 요소(SmartArt/OLE·xfrm없는
   * sp/pic·빈 그룹·mc:AlternateContent)를 사용자 삭제로 오인해 지우는 것을 막는다.
   */
  deletedCNvIds?: number[];
  /** 편집기가 새로 만든 슬라이드(XML 파트 없음) — 패처가 빈 파트를 합성한 뒤
   *  reconcileShapeTree 가 모델 도형(레이아웃 placeholder 복제분)을 채운다. */
  synthetic?: boolean;
  /** p:sld/@show="0" — 슬라이드 쇼에서 건너뛰는 숨김 슬라이드. */
  hidden?: boolean;
  /** 편집기가 hidden 을 토글했을 때만 true(패처 @show 되쓰기 게이트). */
  hiddenDirty?: boolean;
  /** 편집기가 배경을 바꿨을 때만 true(패처 p:bg 되쓰기 게이트). */
  backgroundDirty?: boolean;
  /** 편집기가 레이아웃을 바꿨을 때만 true(패처 slideLayout rel 교체 게이트). */
  layoutDirty?: boolean;
  /** 슬라이드 붙여넣기용 원본 파트 스냅샷 — 잘라내기(원본 파트가 이후 저장에서
   *  삭제됨) 뒤에도 붙여넣기가 가능하도록 복사 시점 바이트를 클립보드에 보관,
   *  패처 복제 시 duplicatedFromPart 조회보다 우선한다. */
  duplicateSourceSnapshot?: {
    xml: string;
    relsXml?: string | null;
    notesXml?: string | null;
    notesRelsXml?: string | null;
  };
  /** 슬라이드 복제 원본의 partPath — 패처가 원본 파트 바이트를 복제해 새 파트를
   *  만든다(파서가 모델에 안 담은 요소까지 보존). 복제 후 편집분은 dirty 플래그로
   *  복제 파트에 그대로 패치된다. */
  duplicatedFromPart?: string;
}

export interface PptxImageCrop {
  left: number;    // 0~100000 (100000 = 100%)
  top: number;
  right: number;
  bottom: number;
}

export interface PptxShape {
  /** Full modeled fill/outline replacement by the editor's format painter only. */
  formatPainterDirty?: boolean;
  id: string;
  /**
   * cNvPr@id 를 순수 숫자로 노출한 앵커. 슬라이드 파트 내에서 유일하므로
   * 패처가 positional-index 대신 이 값으로 XML 요소를 매칭한다(그룹/표 포함
   * 슬라이드 무음손상 방지). cNvPr 에 숫자 id 가 없어 genId 로 폴백한 도형은
   * null(anchorless) — 패처가 형별 positional 폴백으로 처리한다.
   */
  cNvId?: number | null;
  name: string;
  type: 'textbox' | 'image' | 'shape' | 'table' | 'group' | 'connector' | 'chart';
  position: { x: number; y: number };
  size: { width: number; height: number };
  hasExplicitTransform?: boolean;
  rotation?: number;
  flipH?: boolean;
  flipV?: boolean;
  textBody?: PptxTextBody;
  geometry?: PptxGeometry;
  fill?: PptxFill;
  outline?: PptxOutline;
  imageRId?: string;
  imageCrop?: PptxImageCrop;
  /** User-edited picture crop/source; patch only srcRect or blip while preserving native metadata. */
  imageCropDirty?: boolean;
  imageSourceDirty?: boolean;
  placeholder?: { type: string; idx?: string };
  /** a:effectLst/a:outerShdw (명시) 또는 p:style/a:effectRef (테마 effectStyleLst 해석) 그림자 */
  shadow?: PptxShadow;
  children?: PptxShape[];
  tableGrid?: PptxTableGrid;
  groupChildOffset?: { x: number; y: number };
  groupChildExtent?: { width: number; height: number };
  /** graphicFrame 차트의 rId — parsePptx 후처리가 chart 로 해석 */
  chartRId?: string;
  /** 해석된 차트 모델 (렌더러 PptxChartShape 소비) */
  chart?: PptxChart;
  /** 편집기가 fill/outline 을 실제로 변경했을 때만 true — PptxRun.dirty 와 동일 계약. */
  dirty?: boolean;
  /**
   * 편집기가 위치/크기/회전(xfrm)을 실제로 변경했을 때만 true. 패처는 이 플래그가
   * 선 도형에만 off/ext/rot/flip 을 되쓴다 — 무변형 도형(특히 placeholder)의 xfrm
   * 상속(layout/master)을 원본 그대로 보존하기 위함(dirty 와 독립 게이트).
   */
  transformDirty?: boolean;
  /**
   * 편집기가 prstGeom/avLst 조정값(커넥터 꺾임 등)을 변경했을 때만 true. 패처가
   * 이 플래그가 선 도형에만 geometry.adjustments 를 avLst gd 로 되쓴다(무변경
   * 도형의 avLst 상속/원본을 보존 — transformDirty/dirty 와 독립 게이트).
   */
  adjustmentsDirty?: boolean;
  /**
   * 편집기가 새로 추가한 도형 — 원본 XML 에 대응 요소가 없다. 패처
   * (reconcileShapeTree)가 모델 값으로 최소 p:sp 를 합성한다. 저장→재파싱 후엔
   * 정식 파싱 도형(cNvId 매칭)이 되므로 이 플래그는 세션 인메모리에서만 의미.
   */
  synthetic?: boolean;
  /**
   * 복제로 만든 도형 — 이 cNvId(원본 도형의 앵커)의 XML 요소를 clone 해 새
   * cNvId/위치로 붙인다(원본 스타일 완전 보존). 원본이 synthetic/삭제라 XML 이
   * 없으면 synthetic 합성으로 폴백. 그룹/표/차트는 중첩 cNvPr 재할당이 필요해
   * 복제 대상에서 제외한다.
   */
  duplicatedFrom?: number;
  /**
   * 커넥터 글루(연결) — 시작/끝점이 어느 도형의 어느 연결 사이트(idx)에 붙었는지.
   * targetCNvId = 대상 도형의 cNvPr@id(숫자, cNvId 와 같은 공간). idx = preset 로컬
   * 연결점 인덱스(rect/roundRect: 0=위,1=왼,2=아래,3=오른 — 실덱 실증). a:stCxn/
   * a:endCxn 로 파싱·되쓰기. 편집기가 글루를 바꾸면 connectionsDirty 로 게이트.
   */
  startConnection?: { targetCNvId: number; idx: number };
  endConnection?: { targetCNvId: number; idx: number };
  /** 편집기가 커넥터 글루(stCxn/endCxn)를 바꿨을 때만 true(패처 되쓰기 게이트). */
  connectionsDirty?: boolean;
  /**
   * 편집기가 지오메트리를 custGeom 으로 교체했을 때만 true(커넥터 직교 재경로).
   * 패처가 prstGeom→custGeom 으로 되쓴다(transformDirty 로 xfrm 도 함께 리셋).
   */
  geometryDirty?: boolean;
}

export interface PptxParagraphDefaults {
  fontSize?: number;
  fontFamily?: string;
  /** 동아시아(한글 등) 폰트 — defRPr/endParaRPr 의 a:ea (테마 참조 해석 후) */
  eaFontFamily?: string;
  /** complex script 폰트 — defRPr/endParaRPr 의 a:cs (테마 참조 해석 후) */
  csFontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

export interface PptxGeometry {
  preset?: string;
  paths?: string[];
  /**
   * preset='custom' 일 때 a:custGeom/a:pathLst 변환 결과.
   * 렌더러는 각 경로를 viewBox(w×h) → 도형 박스로 스케일해 그린다.
   */
  custom?: PptxCustomGeomPath[];
  /** prstGeom/avLst 의 gd 조정값 (name → val 정수). pie/arc 각도 등. */
  adjustments?: Record<string, number>;
}

/** custGeom 의 a:path 1개를 SVG path 로 변환한 결과 (좌표는 path 좌표 공간 단위 그대로) */
export interface PptxCustomGeomPath {
  /** SVG path data — moveTo/lnTo/cubicBezTo/quadBezTo/arcTo/close 변환 결과 */
  d: string;
  /** a:path w — 경로 좌표 공간 폭. 속성 부재 시 도형 크기(EMU)로 폴백 */
  w: number;
  /** a:path h — 경로 좌표 공간 높이. 속성 부재 시 도형 크기(EMU)로 폴백 */
  h: number;
  /** a:path fill="none" 이면 false — 렌더러는 윤곽선만 그린다 */
  filled: boolean;
  /** a:path stroke="0"/"false" 이면 false — 렌더러는 채움만 그린다 */
  stroked: boolean;
}

/** a:outerShdw 그림자 — 렌더 단위(px)로 환산 완료된 값 */
export interface PptxShadow {
  /** 해석된 그림자 색 (alpha 반영 시 rgba 문자열) */
  color: string;
  /** blurRad → px */
  blur: number;
  /** dist/dir(시계방향, 3시 기준)로 계산한 x 오프셋 (px) */
  offsetX: number;
  /** dist/dir 로 계산한 y 오프셋 (px, 화면 좌표계 — 아래가 양수) */
  offsetY: number;
}

export interface PptxFill {
  /** 'group' 은 a:grpFill(부모 그룹 채움 상속) 마커 — 파서가 그룹 파싱 시 실제 채움으로 치환한다. */
  type: 'solid' | 'gradient' | 'pattern' | 'picture' | 'none' | 'group';
  color?: string;
  gradientStops?: { position: number; color: string }[];
  /** 선형 그라디언트 각도(도 단위, 시계방향·3시 기준). a:lin ang(1/60000도)에서 변환 */
  gradientAngle?: number;
  /** 그라디언트 종류 — a:lin → 'linear', a:path(circle/rect/shape) → 'radial' */
  gradientKind?: 'linear' | 'radial';
  /** picture: a:blip r:embed 관계 id (파트 rels 기준) */
  imageRId?: string;
  /** picture: imageRId 해석 결과 data URL — parsePptx 후처리에서 주입 */
  imageDataUrl?: string;
  /** picture: a:srcRect 크롭 (0~100000 = 0~100%) */
  srcRect?: PptxImageCrop;
  /** picture: a:tile 존재 시 true (부재/stretch 는 undefined) */
  tile?: boolean;
  /** picture(tile): a:tile sx/sy 스케일 (0~1 소수, 100000 = 1.0) */
  tileScale?: { sx: number; sy: number };
  /** pattern: a:pattFill prst (예: 'ltUpDiag') */
  patternPreset?: string;
  /** pattern: 전경색 (a:fgClr) */
  patternFgColor?: string;
  /** pattern: 배경색 (a:bgClr) */
  patternBgColor?: string;
}

/** a:headEnd/a:tailEnd — 선 끝 장식(화살표 등). type 'none' 은 파서가 생략. */
export interface PptxLineEnd {
  /** triangle | stealth | arrow | oval | diamond */
  type: string;
  /** sm | med | lg (기본 med) */
  w?: string;
  /** sm | med | lg (기본 med) */
  len?: string;
}

export interface PptxOutline {
  width?: number;
  color?: string;
  dashStyle?: string;
  /** a:custDash — [dash, gap, ...] 선폭 배수 (ds d/sp = 1000분의 1 %) */
  customDash?: number[];
  /** a:ln cap — rnd | sq | flat */
  cap?: string;
  /** 선 시작점 장식 (커넥터 화살표) */
  headEnd?: PptxLineEnd;
  /** 선 끝점 장식 (커넥터 화살표) */
  tailEnd?: PptxLineEnd;
}

export interface PptxBackground {
  fill?: PptxFill;
  /** Reviewed background copied within this package; never replaces slide content. */
  nativeSource?: {
    xml: string;
    images: Array<{ relationshipId: string; partPath: string }>;
  };
}

export interface PptxTextBody {
  /** Explicit editor body formatting; ordinary edits preserve raw bodyPr. */
  bodyPropsDirty?: boolean;
  paragraphs: PptxParagraph[];
  bodyProps: PptxBodyProps;
  /** 편집기가 문단 수를 바꿨을 때만 true(셀 병합의 문단 이동 등) — 패처의
   *  a:p 구조 동기화(추가/제거) 게이트. 무편집 no-op 보장을 위해 기본 미설정. */
  structureDirty?: boolean;
}

export interface PptxBodyProps {
  anchor?: string;
  anchorCtr?: boolean;
  wrap?: string;
  horzOverflow?: string;
  vertOverflow?: string;
  lIns?: number;
  tIns?: number;
  rIns?: number;
  bIns?: number;
  autofit?: 'none' | 'normal' | 'shrink';
  /** normAutofit fontScale (0~1 소수, 1000분의 1% 단위 → 100000 = 1.0). 부재 시 1.0 취급 */
  autofitFontScale?: number;
  /** normAutofit lnSpcReduction (0~1 소수). 부재 시 0 취급 */
  autofitLnSpcReduction?: number;
}

export interface PptxParagraph {
  /** Full modeled paragraph formatting replacement by format painter only. */
  formatPainterDirty?: boolean;
  id: string;
  runs: PptxRun[];
  alignment?: string;
  bulletType?: 'none' | 'char' | 'autoNum';
  bulletChar?: string;
  bulletAutoNumType?: string;
  bulletFontFamily?: string;
  /** 편집기가 줄 간격을 바꿨을 때만 true — 패처가 a:lnSpc 를 되쓰는 게이트
   *  (모델 spacing 은 상속 해석값일 수 있어 무조건 쓰면 의미 변경). */
  spacingDirty?: boolean;
  /** 편집기가 글머리 기호를 바꿨을 때만 true — 패처 bullet 되쓰기 게이트. */
  bulletDirty?: boolean;
  /** buClr — 불릿 색 (해석 완료 hex/rgba). 미지정 시 텍스트 색을 따름. */
  bulletColor?: string;
  /** buSzPts — 불릿 절대 크기(pt). PowerPoint/LO 에서 불릿 크기는 행간에 영향 없음. */
  bulletSizePts?: number;
  /** buSzPct — 첫 텍스트 런 크기 대비 비율(0~n, 1=100%). */
  bulletSizePct?: number;
  level?: number;
  spacing?: { before?: number; after?: number; lineSpacing?: number };
  indent?: {
    left?: number;      // marL: 텍스트 왼쪽 마진 (px)
    hanging?: number;   // 양수: 첫 줄 왼쪽으로 내어쓰기(hanging) 크기 (px) → textIndent에 음수로 적용
    firstLine?: number; // 양수: 첫 줄 들여쓰기 크기 (px) → textIndent에 양수로 적용
  };
  defaultRunProps?: PptxParagraphDefaults;
  /** 편집기가 문단 속성(alignment)을 실제로 변경했을 때만 true — PptxRun.dirty 와 동일 계약. */
  dirty?: boolean;
}

export interface PptxRun {
  /** Explicit language-font change (Latin/EA/CS); separate from other character styles. */
  fontFamiliesDirty?: boolean;
  /** Full modeled character formatting replacement; hyperlink children stay intact. */
  formatPainterDirty?: boolean;
  id: string;
  type: 'text' | 'break' | 'field';
  /** a:fld 의 type 속성 (예: 'slidenum', 'datetime1') — type==='field' 일 때만. */
  fieldType?: string;
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** a:rPr@baseline — 위첨자(양수)/아래첨자(음수), 1000분의 1% (30000 = 30% 위). */
  baseline?: number;
  fontSize?: number;
  fontFamily?: string;
  /** 동아시아(한글 등) 폰트 — rPr 의 a:ea typeface (테마 참조 '+mn-ea' 등 해석 후) */
  eaFontFamily?: string;
  /** complex script 폰트 — rPr 의 a:cs typeface (테마 참조 해석 후) */
  csFontFamily?: string;
  color?: string;
  highlight?: string;
  /**
   * 편집기(usePptxEditor)가 이 런의 **서식을 실제로 변경**했을 때만 true.
   * 파서는 절대 세우지 않는다. 패처(pptxPatcher.patchRun)는 이 플래그가
   * 있을 때만 rPr 을 다시 쓴다 — 모델의 서식 값은 테마/마스터에서 "해석된"
   * 값이라, 무조건 되쓰면 원본에 없던 명시 속성이 구워져(schemeClr→srgbClr,
   * 테마폰트→해석명) 테마 연결이 끊기는 의미 변경이 되기 때문.
   */
  dirty?: boolean;
}

export interface PptxTableGrid {
  rows: PptxTableRow[];
  gridCols: number[];
  /** tblPr 의 표 스타일 참조 — parsePptx 후처리(applyTableStyleToGrid)가 소비 */
  styleRef?: PptxTableStyleRef;
  /** 편집기가 행/열 구조를 바꿨을 때만 true — 패처의 tr/tc 구조 동기화 게이트 */
  structureDirty?: boolean;
}

export interface PptxTableRow {
  height: number;
  cells: PptxTableCell[];
  /** 파스 시점 XML tr 인덱스 — 패처가 원본 tr 을 재배열·재사용(충실도 보존)하는
   *  키. 편집기가 삽입한 신규 행은 undefined(패처가 인접 행 tr 클론으로 합성). */
  srcIndex?: number;
}

export interface PptxTableCell {
  textBody?: PptxTextBody;
  /** 파스 시점 행 내 XML tc 인덱스 — srcIndex 규약은 PptxTableRow 와 동일 */
  srcIndex?: number;
  /** 편집기가 셀 채움을 바꿨을 때만 true — 패처의 tcPr solidFill 되쓰기 게이트 */
  fillDirty?: boolean;
  /** 편집기가 셀 테두리를 바꿨을 때만 true — 패처의 tcPr lnL/R/T/B 되쓰기 게이트 */
  bordersDirty?: boolean;
  colSpan?: number;
  rowSpan?: number;
  hMerge?: boolean;
  vMerge?: boolean;
  fill?: PptxFill;
  borders?: {
    top?: PptxBorder;
    bottom?: PptxBorder;
    left?: PptxBorder;
    right?: PptxBorder;
  };
  padding?: { left: number; right: number; top: number; bottom: number };
  anchor?: string;
}

export interface PptxBorder {
  width?: number;
  color?: string;
  style?: string;
}

/** tableStyles.xml 의 스타일 파트 1개(wholeTbl/firstRow/band1H …)의 해석 결과 */
export interface PptxTableStylePart {
  /** tcStyle/fill/solidFill 해석색 */
  fillColor?: string;
  /** tcStyle/tcBdr — 표 영역 기준 테두리(outer 4방향 + 셀 사이 inside) */
  borders?: {
    left?: PptxBorder;
    right?: PptxBorder;
    top?: PptxBorder;
    bottom?: PptxBorder;
    insideH?: PptxBorder;
    insideV?: PptxBorder;
  };
  /** tcTxStyle b="on" */
  bold?: boolean;
}

/** tableStyles.xml 의 tblStyle 1개 (파트별 스타일) */
export interface PptxTableStyle {
  wholeTbl?: PptxTableStylePart;
  band1H?: PptxTableStylePart;
  band2H?: PptxTableStylePart;
  firstRow?: PptxTableStylePart;
  lastRow?: PptxTableStylePart;
  firstCol?: PptxTableStylePart;
  lastCol?: PptxTableStylePart;
}

/** a:tbl 의 tblPr — 표 스타일 적용 플래그 + 스타일 ID */
export interface PptxTableStyleRef {
  styleId?: string;
  firstRow?: boolean;
  lastRow?: boolean;
  bandRow?: boolean;
  firstCol?: boolean;
  lastCol?: boolean;
  bandCol?: boolean;
}

export interface PptxTheme {
  name: string;
  colors: PptxThemeColors;
  fonts: {
    major: string;
    minor: string;
    /** majorFont 의 <a:ea> typeface ('+mj-ea' 해석 대상, 빈 문자열이면 부재) */
    majorEa?: string;
    /** minorFont 의 <a:ea> typeface ('+mn-ea' 해석 대상) */
    minorEa?: string;
    /** majorFont 의 <a:cs> typeface ('+mj-cs' 해석 대상) */
    majorCs?: string;
    /** minorFont 의 <a:cs> typeface ('+mn-cs' 해석 대상) */
    minorCs?: string;
  };
  colorMap: PptxColorMap;
  lineStyles: PptxOutline[];
  /**
   * fmtScheme/fillStyleLst 원본 요소 — fillRef idx(1~999) 해석용.
   * phClr 치환이 참조 시점에야 가능해 원본 Element 를 보관한다 (파서 내부 소비 전용,
   * 렌더러는 해석 결과인 PptxShape.fill / PptxBackground.fill 만 보면 된다).
   */
  fillStyleEls?: Element[];
  /** fmtScheme/bgFillStyleLst 원본 요소 — bgRef·fillRef idx(1001~) 해석용 (파서 내부 소비 전용) */
  bgFillStyleEls?: Element[];
  /** fmtScheme/effectStyleLst 원본 요소 — effectRef idx 해석용 (파서 내부 소비 전용) */
  effectStyleEls?: Element[];
}

export interface PptxThemeColors {
  dk1: string;
  lt1: string;
  dk2: string;
  lt2: string;
  accent1: string;
  accent2: string;
  accent3: string;
  accent4: string;
  accent5: string;
  accent6: string;
  hlink: string;
  folHlink: string;
}

export interface PptxColorMap {
  bg1: keyof PptxThemeColors;
  tx1: keyof PptxThemeColors;
  bg2: keyof PptxThemeColors;
  tx2: keyof PptxThemeColors;
  accent1: keyof PptxThemeColors;
  accent2: keyof PptxThemeColors;
  accent3: keyof PptxThemeColors;
  accent4: keyof PptxThemeColors;
  accent5: keyof PptxThemeColors;
  accent6: keyof PptxThemeColors;
  hlink: keyof PptxThemeColors;
  folHlink: keyof PptxThemeColors;
}

export interface PptxSlideMaster {
  partPath: string;
  background?: PptxBackground;
  shapes: PptxShape[];
  colorMap?: PptxColorMap;
  themeRef?: string;
  /** p:sldLayoutIdLst 문서순의 레이아웃 partPath — PowerPoint "새 슬라이드"의
   *  제목→두번째 레이아웃 규칙 등 마스터 내 레이아웃 순서가 필요한 로직용. */
  layoutRefs?: string[];
  /** p:txStyles — placeholder 텍스트 스타일 상속 사슬의 마스터 단계(레벨 0-based).
   *  기본 템플릿류는 제목 44pt·본문 불릿 등을 여기 정의한다(ph 도형 lstStyle 아님). */
  textStyles?: {
    title?: Record<number, PptxParagraphStyleDefaults>;
    body?: Record<number, PptxParagraphStyleDefaults>;
    other?: Record<number, PptxParagraphStyleDefaults>;
  };
}

export interface PptxSlideLayout {
  partPath: string;
  background?: PptxBackground;
  shapes: PptxShape[];
  colorMap?: PptxColorMap;
  masterRef?: string;
  /** p:sldLayout/@type (title, obj, tx, …) — 새 슬라이드 레이아웃 선택 규칙용. */
  type?: string;
  /** p:cSld/@name ("Title Slide" 등) — 레이아웃 적용 메뉴 표시명. */
  name?: string;
}

/* ------------------------------------------------------------------ */
/* XML 네임스페이스                                                      */
/* ------------------------------------------------------------------ */

const P_NS = 'http://schemas.openxmlformats.org/presentationml/2006/main';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const C_NS = 'http://schemas.openxmlformats.org/drawingml/2006/chart';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

/* ------------------------------------------------------------------ */
/* 유틸리티                                                              */
/* ------------------------------------------------------------------ */

let _idCounter = 0;
function genId(): string {
  return `pptx_${Date.now()}_${++_idCounter}`;
}

/** EMU → px (1 EMU = 1/914400 inch, 96 DPI) */
function emuToPx(emu: number): number {
  return (emu / 914400) * 96;
}

/** px → EMU */
export function pxToEmu(px: number): number {
  return Math.round((px / 96) * 914400);
}

/** hundredths of a point → px (1 pt = 100 hundredths, 1 pt ≈ 1.333px) */
export function centiPtToPx(cp: number): number {
  return cp / 100;
}

function getEl(parent: Element, ns: string, localName: string): Element | null {
  return parent.getElementsByTagNameNS(ns, localName)[0] || null;
}

function getAllEl(parent: Element, ns: string, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS(ns, localName));
}

function directChildrenNS(parent: Element, ns: string, localName: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes[i];
    if (
      child.nodeType === 1 &&
      (child as Element).localName === localName &&
      (child as Element).namespaceURI === ns
    ) {
      result.push(child as Element);
    }
  }
  return result;
}

function attr(el: Element, name: string): string | null {
  return el.getAttribute(name);
}

async function readPart(zip: JSZip, path: string): Promise<string | null> {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const file = zip.file(cleanPath);
  if (!file) return null;
  return await file.async('string');
}

/* ------------------------------------------------------------------ */
/* 테마 색상 해석                                                        */
/* ------------------------------------------------------------------ */

const DEFAULT_COLOR_MAP: PptxColorMap = {
  bg1: 'lt1',
  tx1: 'dk1',
  bg2: 'lt2',
  tx2: 'dk2',
  accent1: 'accent1',
  accent2: 'accent2',
  accent3: 'accent3',
  accent4: 'accent4',
  accent5: 'accent5',
  accent6: 'accent6',
  hlink: 'hlink',
  folHlink: 'folHlink',
};

const DEFAULT_THEME: PptxTheme = {
  name: 'Default',
  colors: {
    dk1: '#000000', lt1: '#FFFFFF',
    dk2: '#44546A', lt2: '#E7E6E6',
    accent1: '#4472C4', accent2: '#ED7D31',
    accent3: '#A5A5A5', accent4: '#FFC000',
    accent5: '#5B9BD5', accent6: '#70AD47',
    hlink: '#0563C1', folHlink: '#954F72',
  },
  fonts: { major: 'Calibri', minor: 'Calibri' },
  colorMap: { ...DEFAULT_COLOR_MAP },
  lineStyles: [
    { width: 1, color: '#000000' },
    { width: 2, color: '#000000' },
    { width: 3, color: '#000000' },
  ],
};

function parseTheme(xml: string): PptxTheme {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const theme: PptxTheme = {
    ...DEFAULT_THEME,
    colors: { ...DEFAULT_THEME.colors },
    fonts: { ...DEFAULT_THEME.fonts },
    colorMap: { ...DEFAULT_THEME.colorMap },
    lineStyles: DEFAULT_THEME.lineStyles.map((line) => ({ ...line })),
  };

  const themeEl = dom.documentElement;
  theme.name = attr(themeEl, 'name') || 'Default';

  const clrScheme = getEl(dom.documentElement, A_NS, 'clrScheme');
  if (clrScheme) {
    const colorNames: (keyof PptxThemeColors)[] = [
      'dk1', 'lt1', 'dk2', 'lt2',
      'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6',
      'hlink', 'folHlink',
    ];
    for (const name of colorNames) {
      const el = getEl(clrScheme, A_NS, name);
      if (!el) continue;
      const color = extractColorFromElement(el);
      if (color) theme.colors[name] = color;
    }
  }

  // fontScheme 의 latin/ea/cs typeface 파싱 — 빈 문자열("")은 부재로 취급
  // (Office 기본 테마가 <a:cs typeface=""/> 처럼 빈 값을 내보냄)
  const readTypeface = (fontEl: Element, localName: string): string | undefined => {
    const el = getEl(fontEl, A_NS, localName);
    const typeface = el ? attr(el, 'typeface') : null;
    return typeface || undefined;
  };

  const majorFont = getEl(dom.documentElement, A_NS, 'majorFont');
  if (majorFont) {
    const latin = readTypeface(majorFont, 'latin');
    if (latin) theme.fonts.major = latin;
    theme.fonts.majorEa = readTypeface(majorFont, 'ea');
    theme.fonts.majorCs = readTypeface(majorFont, 'cs');
  }

  const minorFont = getEl(dom.documentElement, A_NS, 'minorFont');
  if (minorFont) {
    const latin = readTypeface(minorFont, 'latin');
    if (latin) theme.fonts.minor = latin;
    theme.fonts.minorEa = readTypeface(minorFont, 'ea');
    theme.fonts.minorCs = readTypeface(minorFont, 'cs');
  }

  const lnStyleLst = getEl(dom.documentElement, A_NS, 'lnStyleLst');
  if (lnStyleLst) {
    const parsedLineStyles = directChildrenNS(lnStyleLst, A_NS, 'ln')
      .map((lnEl) => parseOutline(lnEl, theme))
      .filter((line): line is PptxOutline => !!line);
    if (parsedLineStyles.length > 0) {
      theme.lineStyles = parsedLineStyles;
    }
  }

  // fmtScheme 의 fill/bgFill/effect 스타일 목록 — fillRef/bgRef/effectRef 의
  // phClr 치환은 참조 시점에야 가능하므로 원본 Element 를 그대로 보관한다.
  const fmtScheme = getEl(dom.documentElement, A_NS, 'fmtScheme');
  if (fmtScheme) {
    const collectStyleEls = (localName: string): Element[] | undefined => {
      const lstEl = getEl(fmtScheme, A_NS, localName);
      if (!lstEl) return undefined;
      const els: Element[] = [];
      for (let i = 0; i < lstEl.childNodes.length; i++) {
        const child = lstEl.childNodes[i];
        if (child.nodeType === 1) els.push(child as Element);
      }
      return els.length > 0 ? els : undefined;
    };
    theme.fillStyleEls = collectStyleEls('fillStyleLst');
    theme.bgFillStyleEls = collectStyleEls('bgFillStyleLst');
    theme.effectStyleEls = collectStyleEls('effectStyleLst');
  }

  return theme;
}

function resolveThemeTypeface(typeface: string | null, theme: PptxTheme): string | undefined {
  if (!typeface) return undefined;
  // '+mj-ea'/'+mn-ea' 는 테마 동아시아 폰트, '+mj-cs'/'+mn-cs' 는 complex script 폰트.
  // 테마에 해당 슬롯이 없으면 latin(major/minor)으로 폴백.
  if (typeface.startsWith('+mj')) {
    if (typeface === '+mj-ea') return theme.fonts.majorEa ?? theme.fonts.major;
    if (typeface === '+mj-cs') return theme.fonts.majorCs ?? theme.fonts.major;
    return theme.fonts.major;
  }
  if (typeface.startsWith('+mn')) {
    if (typeface === '+mn-ea') return theme.fonts.minorEa ?? theme.fonts.minor;
    if (typeface === '+mn-cs') return theme.fonts.minorCs ?? theme.fonts.minor;
    return theme.fonts.minor;
  }
  return typeface;
}

function isThemeColorKey(value: string | null): value is keyof PptxThemeColors {
  return !!value && value in DEFAULT_THEME.colors;
}

function parseColorMapEl(el: Element | null): Partial<PptxColorMap> | undefined {
  if (!el) return undefined;
  const keys: (keyof PptxColorMap)[] = [
    'bg1', 'tx1', 'bg2', 'tx2',
    'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6',
    'hlink', 'folHlink',
  ];
  const colorMap: Partial<PptxColorMap> = {};
  for (const key of keys) {
    const mapped = attr(el, key);
    if (isThemeColorKey(mapped)) {
      colorMap[key] = mapped;
    }
  }
  return Object.keys(colorMap).length > 0 ? colorMap : undefined;
}

function mergeColorMap(base: PptxColorMap, override?: Partial<PptxColorMap>): PptxColorMap {
  return {
    ...base,
    ...(override || {}),
  };
}

function withColorMap(theme: PptxTheme, colorMap?: PptxColorMap): PptxTheme {
  if (!colorMap) return theme;
  return { ...theme, colorMap };
}

function parseColorMapOverride(rootEl: Element | null): Partial<PptxColorMap> | 'master' | undefined {
  if (!rootEl) return undefined;
  const clrMapOvr = getEl(rootEl, P_NS, 'clrMapOvr') || getEl(rootEl, A_NS, 'clrMapOvr');
  if (!clrMapOvr) return undefined;

  const masterClrMapping = getEl(clrMapOvr, A_NS, 'masterClrMapping')
    || getEl(clrMapOvr, P_NS, 'masterClrMapping');
  if (masterClrMapping) return 'master';

  const overrideClrMapping = getEl(clrMapOvr, A_NS, 'overrideClrMapping')
    || getEl(clrMapOvr, P_NS, 'overrideClrMapping');
  return parseColorMapEl(overrideClrMapping || clrMapOvr);
}

function extractColorFromElement(el: Element): string | null {
  const srgb = getEl(el, A_NS, 'srgbClr');
  if (srgb) {
    const val = attr(srgb, 'val');
    if (val) return `#${val}`;
  }
  const sys = getEl(el, A_NS, 'sysClr');
  if (sys) {
    const lastClr = attr(sys, 'lastClr');
    if (lastClr) return `#${lastClr}`;
  }
  return null;
}

/** scheme color name → hex */
export function resolveSchemeColor(name: string, theme: PptxTheme): string {
  const colorMap = theme.colorMap;
  const key = (name in colorMap
    ? colorMap[name as keyof PptxColorMap]
    : (isThemeColorKey(name) ? name : undefined));
  return key ? theme.colors[key] : '#000000';
}

/* ------------------------------------------------------------------ */
/* 색상 파싱 (여러 형태 지원)                                              */
/* ------------------------------------------------------------------ */

/** prstClr name → hex */
const PRESET_COLORS: Record<string, string> = {
  black: '#000000', white: '#FFFFFF', red: '#FF0000', green: '#008000',
  blue: '#0000FF', yellow: '#FFFF00', cyan: '#00FFFF', magenta: '#FF00FF',
  darkBlue: '#00008B', darkCyan: '#008B8B', darkGray: '#A9A9A9', darkGreen: '#006400',
  darkMagenta: '#8B008B', darkRed: '#8B0000', darkYellow: '#808000',
  gray: '#808080', lightGray: '#D3D3D3', orange: '#FFA500', purple: '#800080',
  pink: '#FFC0CB', brown: '#A52A2A', lime: '#00FF00', silver: '#C0C0C0',
  gold: '#FFD700', ivory: '#FFFFF0', lavender: '#E6E6FA', maroon: '#800000',
  navy: '#000080', olive: '#808000', teal: '#008080', violet: '#EE82EE',
};

/**
 * schemeClr의 lumMod/lumOff/satMod/tint/shade 변환
 * Microsoft OOXML 명세 기준: 0~100000 스케일 (100000 = 100%)
 */
function applySchemeColorModifiers(hexColor: string, modEl: Element): string {
  let r = parseInt(hexColor.slice(1, 3), 16);
  let g = parseInt(hexColor.slice(3, 5), 16);
  let b = parseInt(hexColor.slice(5, 7), 16);

  const lumModEl = getEl(modEl, A_NS, 'lumMod');
  const lumOffEl = getEl(modEl, A_NS, 'lumOff');
  const satModEl = getEl(modEl, A_NS, 'satMod');
  const tintEl = getEl(modEl, A_NS, 'tint');
  const shadeEl = getEl(modEl, A_NS, 'shade');

  // Convert RGB → HLS for lum operations
  const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let l = (max + min) / 2;
  let s = 0, h = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
      case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
      case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
    }
  }

  if (lumModEl) l = l * (parseInt(attr(lumModEl, 'val') || '100000', 10) / 100000);
  if (lumOffEl) l = l + (parseInt(attr(lumOffEl, 'val') || '0', 10) / 100000);
  l = Math.max(0, Math.min(1, l));

  // satMod: 채도 배율 (테마 fmtScheme 의 phClr 그라디언트가 상용 — 예: 300000 = 3배)
  if (satModEl) {
    s = s * (parseInt(attr(satModEl, 'val') || '100000', 10) / 100000);
    s = Math.max(0, Math.min(1, s));
  }

  // HLS → RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (s === 0) {
    r = g = b = Math.round(l * 255);
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    g = Math.round(hue2rgb(p, q, h) * 255);
    b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  }

  // tint: mix toward white
  if (tintEl) {
    const t = parseInt(attr(tintEl, 'val') || '100000', 10) / 100000;
    r = Math.round(r + (255 - r) * (1 - t));
    g = Math.round(g + (255 - g) * (1 - t));
    b = Math.round(b + (255 - b) * (1 - t));
  }
  // shade: mix toward black
  if (shadeEl) {
    const s2 = parseInt(attr(shadeEl, 'val') || '100000', 10) / 100000;
    r = Math.round(r * s2);
    g = Math.round(g * s2);
    b = Math.round(b * s2);
  }

  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

/**
 * alpha 요소를 읽어 rgba() 혹은 hex 반환
 * PPTX alpha: 0 = 완전 투명, 100000 = 완전 불투명
 */
function applyAlpha(hex: string, colorEl: Element): string {
  const alphaEl = getEl(colorEl, A_NS, 'alpha');
  if (!alphaEl) return hex;
  const val = parseInt(attr(alphaEl, 'val') || '100000', 10);
  if (val >= 100000) return hex;
  const opacity = val / 100000;
  if (opacity <= 0) return 'transparent';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity.toFixed(3)})`;
}

/**
 * phClr 치환용 '#RRGGBB' 정규화 — 참조 색이 rgba(alpha 포함)로 해석된 경우
 * 변조(applySchemeColorModifiers)가 hex 를 요구하므로 rgb 성분만 추출한다.
 */
function normalizeToHex(color: string | undefined): string | undefined {
  if (!color) return undefined;
  if (color.startsWith('#')) return color.length >= 7 ? color.slice(0, 7) : color;
  const m = color.match(/^rgba?\((\d+),(\d+),(\d+)/);
  if (m) {
    const toHex = (v: string) => Math.max(0, Math.min(255, parseInt(v, 10))).toString(16).padStart(2, '0');
    return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`.toUpperCase();
  }
  return undefined;
}

function parseColor(el: Element, theme: PptxTheme, phClr?: string): string | undefined {
  const srgb = getEl(el, A_NS, 'srgbClr');
  if (srgb) {
    const val = attr(srgb, 'val');
    if (!val) return undefined;
    const hex = `#${val}`.toUpperCase();
    return applyAlpha(hex, srgb);
  }
  const schemeClr = getEl(el, A_NS, 'schemeClr');
  if (schemeClr) {
    const val = attr(schemeClr, 'val');
    if (val) {
      // 'phClr' 는 스타일 참조(fillRef/bgRef/effectRef)의 자식 색으로 치환
      const baseColor = val === 'phClr'
        ? (normalizeToHex(phClr) ?? '#000000')
        : resolveSchemeColor(val, theme);
      const hasModifier = schemeClr.getElementsByTagNameNS(A_NS, 'lumMod').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'lumOff').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'satMod').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'tint').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'shade').length > 0;
      const hex = hasModifier ? applySchemeColorModifiers(baseColor, schemeClr) : baseColor;
      return applyAlpha(hex, schemeClr);
    }
  }
  const sysClr = getEl(el, A_NS, 'sysClr');
  if (sysClr) {
    const lastClr = attr(sysClr, 'lastClr');
    if (!lastClr) return undefined;
    const hex = `#${lastClr}`.toUpperCase();
    return applyAlpha(hex, sysClr);
  }
  const prstClr = getEl(el, A_NS, 'prstClr');
  if (prstClr) {
    const val = attr(prstClr, 'val');
    if (val) {
      const hex = PRESET_COLORS[val] ?? '#000000';
      return applyAlpha(hex, prstClr);
    }
  }
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Fill 파싱                                                            */
/* ------------------------------------------------------------------ */

/**
 * blipFill 요소(a:blipFill 또는 p:pic 의 p:blipFill)에서 그림 참조 정보를 추출.
 * 내부 요소(a:blip / a:srcRect / a:tile)는 네임스페이스가 항상 A_NS 라
 * 부모 네임스페이스와 무관하게 공용으로 쓸 수 있다 (parsePicture 와 parseFill 공유).
 */
function parseBlipRef(blipFillEl: Element): {
  rId?: string;
  srcRect?: PptxImageCrop;
  tile?: boolean;
  tileScale?: { sx: number; sy: number };
} {
  let rId: string | undefined;
  const blip = getEl(blipFillEl, A_NS, 'blip');
  if (blip) {
    rId = blip.getAttributeNS(R_NS, 'embed') || attr(blip, 'r:embed') || undefined;
  }

  let srcRect: PptxImageCrop | undefined;
  const srcRectEl = getEl(blipFillEl, A_NS, 'srcRect');
  if (srcRectEl) {
    const l = parseInt(attr(srcRectEl, 'l') || '0', 10);
    const t = parseInt(attr(srcRectEl, 't') || '0', 10);
    const r = parseInt(attr(srcRectEl, 'r') || '0', 10);
    const b = parseInt(attr(srcRectEl, 'b') || '0', 10);
    if (l !== 0 || t !== 0 || r !== 0 || b !== 0) {
      srcRect = { left: l, top: t, right: r, bottom: b };
    }
  }

  let tile: boolean | undefined;
  let tileScale: { sx: number; sy: number } | undefined;
  const tileEl = getEl(blipFillEl, A_NS, 'tile');
  if (tileEl) {
    tile = true;
    const sx = parseInt(attr(tileEl, 'sx') || '100000', 10);
    const sy = parseInt(attr(tileEl, 'sy') || '100000', 10);
    tileScale = { sx: sx / 100000, sy: sy / 100000 };
  }

  return { rId, srcRect, tile, tileScale };
}

/**
 * fill 요소(a:solidFill/a:gradFill/a:blipFill/a:pattFill/a:noFill) 자체를 파싱.
 * phClr 는 테마 fmtScheme 스타일(fillRef/bgRef) 해석 시 참조 색 치환에 쓰인다.
 */
function parseFillFromElement(fillEl: Element, theme: PptxTheme, phClr?: string): PptxFill | undefined {
  switch (fillEl.localName) {
    case 'solidFill':
      return { type: 'solid', color: parseColor(fillEl, theme, phClr) };
    case 'gradFill': {
      const stops: PptxFill['gradientStops'] = [];
      const gsLst = getEl(fillEl, A_NS, 'gsLst');
      if (gsLst) {
        const gsEls = directChildrenNS(gsLst, A_NS, 'gs');
        for (const gs of gsEls) {
          const pos = parseInt(attr(gs, 'pos') || '0', 10) / 1000;
          const color = parseColor(gs, theme, phClr);
          if (color) stops.push({ position: pos, color });
        }
      }
      const fill: PptxFill = { type: 'gradient', gradientStops: stops };
      // a:lin → 선형 (ang 은 1/60000도, 시계방향·3시 기준) / a:path → 방사형 근사
      const lin = directChildrenNS(fillEl, A_NS, 'lin')[0];
      const pathEl = directChildrenNS(fillEl, A_NS, 'path')[0];
      if (lin) {
        fill.gradientKind = 'linear';
        const ang = attr(lin, 'ang');
        fill.gradientAngle = ang ? parseInt(ang, 10) / 60000 : 0;
      } else if (pathEl) {
        // path="circle"|"rect"|"shape" 전부 방사형으로 근사 렌더
        fill.gradientKind = 'radial';
      }
      return fill;
    }
    case 'blipFill': {
      // 그림 채움 — 도형 spPr / 표 셀 tcPr / 배경 bgPr 의 a:blipFill
      const blipRef = parseBlipRef(fillEl);
      return {
        type: 'picture',
        imageRId: blipRef.rId,
        srcRect: blipRef.srcRect,
        tile: blipRef.tile,
        tileScale: blipRef.tileScale,
      };
    }
    case 'pattFill': {
      // 패턴 채움 — 근사 렌더용 최소 정보 (prst + 전경/배경색)
      const fgClr = getEl(fillEl, A_NS, 'fgClr');
      const bgClr = getEl(fillEl, A_NS, 'bgClr');
      return {
        type: 'pattern',
        patternPreset: attr(fillEl, 'prst') || undefined,
        patternFgColor: fgClr ? parseColor(fgClr, theme, phClr) : undefined,
        patternBgColor: bgClr ? parseColor(bgClr, theme, phClr) : undefined,
      };
    }
    case 'noFill':
      return { type: 'none' };
    case 'grpFill':
      // 부모 그룹의 채움 상속 — parseGroupShape 가 그룹 fill 로 치환한다
      return { type: 'group' };
    default:
      return undefined;
  }
}

function parseFill(parent: Element, theme: PptxTheme, phClr?: string): PptxFill | undefined {
  // 기존 계약 유지: 문서 순서가 아니라 solid → grad → blip → patt → noFill 순으로 탐색
  const fillNames = ['solidFill', 'gradFill', 'blipFill', 'pattFill', 'noFill', 'grpFill'];
  for (const name of fillNames) {
    const fillEl = directChildrenNS(parent, A_NS, name)[0];
    if (fillEl) return parseFillFromElement(fillEl, theme, phClr);
  }
  return undefined;
}

/**
 * p:style 의 a:fillRef / p:bg 의 p:bgRef 를 테마 fmtScheme 채움으로 해석.
 * - idx 0/1000: 채움 없음 → undefined
 * - idx 1~999: fillStyleLst[idx-1], idx 1001~: bgFillStyleLst[idx-1001]
 * - 목록 부재(테마에 fmtScheme 없음)나 인덱스 초과 시 기존 동작(참조 색 solid) 폴백
 */
function resolveStyleRefFill(refEl: Element, theme: PptxTheme): PptxFill | undefined {
  const idx = parseInt(attr(refEl, 'idx') || '0', 10);
  const phClr = parseColor(refEl, theme);
  if (!Number.isFinite(idx) || idx <= 0 || idx === 1000) return undefined;

  const fillEl = idx >= 1001
    ? theme.bgFillStyleEls?.[idx - 1001]
    : theme.fillStyleEls?.[idx - 1];
  if (fillEl) {
    const fill = parseFillFromElement(fillEl, theme, phClr);
    if (fill) return fill;
  }
  return phClr ? { type: 'solid', color: phClr } : undefined;
}

/* ------------------------------------------------------------------ */
/* Outline 파싱                                                         */
/* ------------------------------------------------------------------ */

function parseOutline(lnEl: Element, theme: PptxTheme): PptxOutline | undefined {
  const noFill = getEl(lnEl, A_NS, 'noFill');
  if (noFill) return undefined;

  const outline: PptxOutline = {};
  const w = attr(lnEl, 'w');
  if (w) outline.width = emuToPx(parseInt(w, 10));

  const solidFill = getEl(lnEl, A_NS, 'solidFill');
  if (solidFill) {
    const color = parseColor(solidFill, theme);
    // transparent (alpha=0) → effectively no fill
    if (color === 'transparent') return undefined;
    outline.color = color;
  }

  const prstDash = getEl(lnEl, A_NS, 'prstDash');
  if (prstDash) outline.dashStyle = attr(prstDash, 'val') || undefined;

  // 커스텀 점선 — a:custDash/a:ds d·sp (1000분의 1 % → 선폭 배수)
  const custDash = getEl(lnEl, A_NS, 'custDash');
  if (custDash) {
    const pattern: number[] = [];
    const dsEls = custDash.getElementsByTagNameNS(A_NS, 'ds');
    for (let i = 0; i < dsEls.length; i++) {
      const d = parseInt(attr(dsEls[i], 'd') || '', 10);
      const sp = parseInt(attr(dsEls[i], 'sp') || '', 10);
      if (Number.isFinite(d) && Number.isFinite(sp)) {
        pattern.push(d / 100000, sp / 100000);
      }
    }
    if (pattern.length > 0) outline.customDash = pattern;
  }

  const cap = attr(lnEl, 'cap');
  if (cap) outline.cap = cap;

  // 선 끝 장식(화살표). type="none" 은 장식 없음이므로 생략한다.
  const parseLineEnd = (name: 'headEnd' | 'tailEnd'): PptxLineEnd | undefined => {
    const el = getEl(lnEl, A_NS, name);
    if (!el) return undefined;
    const type = attr(el, 'type');
    if (!type || type === 'none') return undefined;
    return {
      type,
      w: attr(el, 'w') || undefined,
      len: attr(el, 'len') || undefined,
    };
  };
  outline.headEnd = parseLineEnd('headEnd');
  outline.tailEnd = parseLineEnd('tailEnd');

  return outline.width || outline.color || outline.headEnd || outline.tailEnd
    ? outline
    : undefined;
}

/* ------------------------------------------------------------------ */
/* 그림자(effect) 파싱                                                   */
/* ------------------------------------------------------------------ */

/**
 * a:effectLst 의 a:outerShdw 를 렌더 단위로 환산.
 * dir 은 1/60000도(시계방향·3시 기준), dist/blurRad 는 EMU.
 * 화면 좌표계(y 아래 양수)와 방향 정의가 일치해 offsetY = dist·sin(dir).
 */
function parseOuterShadow(effectLstEl: Element, theme: PptxTheme, phClr?: string): PptxShadow | undefined {
  const outerShdw = getEl(effectLstEl, A_NS, 'outerShdw');
  if (!outerShdw) return undefined;

  const blur = emuToPx(parseInt(attr(outerShdw, 'blurRad') || '0', 10));
  const dist = emuToPx(parseInt(attr(outerShdw, 'dist') || '0', 10));
  const dirDeg = parseInt(attr(outerShdw, 'dir') || '0', 10) / 60000;
  const dirRad = (dirDeg * Math.PI) / 180;
  const color = parseColor(outerShdw, theme, phClr) || 'rgba(0,0,0,0.400)';

  return {
    color,
    blur,
    offsetX: dist * Math.cos(dirRad),
    offsetY: dist * Math.sin(dirRad),
  };
}

/** p:style 의 a:effectRef 를 테마 fmtScheme effectStyleLst 의 outerShdw 로 해석 */
function resolveStyleRefShadow(refEl: Element, theme: PptxTheme): PptxShadow | undefined {
  const idx = parseInt(attr(refEl, 'idx') || '0', 10);
  if (!Number.isFinite(idx) || idx <= 0) return undefined;
  const effectStyleEl = theme.effectStyleEls?.[idx - 1];
  if (!effectStyleEl) return undefined;
  const effectLst = getEl(effectStyleEl, A_NS, 'effectLst');
  if (!effectLst) return undefined;
  const phClr = parseColor(refEl, theme);
  return parseOuterShadow(effectLst, theme, phClr);
}

/* ------------------------------------------------------------------ */
/* _rels 파싱                                                           */
/* ------------------------------------------------------------------ */

function parseRels(xml: string): Map<string, string> {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const map = new Map<string, string>();
  const relEls = dom.getElementsByTagNameNS(REL_NS, 'Relationship');
  for (let i = 0; i < relEls.length; i++) {
    const el = relEls[i];
    const id = attr(el, 'Id');
    const target = attr(el, 'Target');
    if (id && target) map.set(id, target);
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* 미디어 로드                                                          */
/* ------------------------------------------------------------------ */

/** Resolve a relative path against a base directory (handles ../) */
function resolvePath(base: string, rel: string): string {
  if (rel.startsWith('/')) return rel.slice(1);
  const parts = base.split('/');
  const relParts = rel.split('/');
  for (const seg of relParts) {
    if (seg === '..') parts.pop();
    else if (seg !== '.') parts.push(seg);
  }
  return parts.join('/');
}

/** 문자열 → base64 (유니코드 안전 — data URL 용) */
function base64Encode(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

async function loadMedia(
  zip: JSZip,
  partRels: Map<string, Map<string, string>>,
): Promise<Map<string, string>> {
  const media = new Map<string, string>();
  const fileCache = new Map<string, string>();

  for (const [partPath, rels] of partRels) {
    const dir = partPath.substring(0, partPath.lastIndexOf('/'));
    for (const [rId, target] of rels) {
      if (!target.includes('media/') && !target.endsWith('.png') && !target.endsWith('.jpg') &&
          !target.endsWith('.jpeg') && !target.endsWith('.gif') && !target.endsWith('.svg') &&
          !target.endsWith('.bmp') && !target.endsWith('.emf') && !target.endsWith('.wmf') &&
          !target.endsWith('.wdp') && !target.endsWith('.tiff') && !target.endsWith('.tif')) continue;

      const normalizedPath = resolvePath(dir, target);
      const key = `${partPath}::${rId}`;

      if (fileCache.has(normalizedPath)) {
        media.set(key, fileCache.get(normalizedPath)!);
        continue;
      }

      const file = zip.file(normalizedPath);
      if (!file) continue;

      const ext = normalizedPath.split('.').pop()?.toLowerCase() || 'png';

      // EMF 는 브라우저가 디코드 못한다 — 최소 변환기로 SVG 화(성공 시).
      // 실패하면 기존 x-emf data URL 로 두어 렌더러의 자리표시자 폴백 유지.
      if (ext === 'emf') {
        try {
          const emfBuf = await file.async('arraybuffer');
          const svg = emfToSvg(emfBuf);
          if (svg) {
            const svgUrl = `data:image/svg+xml;base64,${base64Encode(svg)}`;
            media.set(key, svgUrl);
            fileCache.set(normalizedPath, svgUrl);
            continue;
          }
        } catch {
          // 변환 실패 — 아래 일반 경로(자리표시자 폴백)로
        }
      }

      const data = await file.async('base64');
      const mimeMap: Record<string, string> = {
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
        gif: 'image/gif', bmp: 'image/bmp', svg: 'image/svg+xml',
        emf: 'image/x-emf', wmf: 'image/x-wmf',
        tiff: 'image/tiff', tif: 'image/tiff',
        // JPEG XR(HD Photo) — 브라우저 디코드 불가 → onError 자리표시자 폴백
        wdp: 'image/vnd.ms-photo',
      };
      const mime = mimeMap[ext] || 'image/png';
      const dataUrl = `data:${mime};base64,${data}`;
      media.set(key, dataUrl);
      fileCache.set(normalizedPath, dataUrl);
    }
  }

  return media;
}

/* ------------------------------------------------------------------ */
/* TextBody 파싱                                                        */
/* ------------------------------------------------------------------ */

function mergeParagraphDefaults(
  primary?: PptxParagraphDefaults,
  fallback?: PptxParagraphDefaults,
): PptxParagraphDefaults | undefined {
  if (!primary && !fallback) return undefined;
  return {
    fontSize: primary?.fontSize ?? fallback?.fontSize,
    fontFamily: primary?.fontFamily ?? fallback?.fontFamily,
    eaFontFamily: primary?.eaFontFamily ?? fallback?.eaFontFamily,
    csFontFamily: primary?.csFontFamily ?? fallback?.csFontFamily,
    bold: primary?.bold ?? fallback?.bold,
    italic: primary?.italic ?? fallback?.italic,
    color: primary?.color ?? fallback?.color,
  };
}

export type PptxParagraphStyleDefaults = Pick<
  PptxParagraph,
  'alignment'
  | 'bulletType'
  | 'bulletChar'
  | 'bulletAutoNumType'
  | 'bulletFontFamily'
  | 'bulletColor'
  | 'bulletSizePts'
  | 'bulletSizePct'
  | 'spacing'
  | 'indent'
  | 'defaultRunProps'
>;

function parseParagraphStyleDefaultsFromPPr(
  pPr: Element,
  theme: PptxTheme,
): PptxParagraphStyleDefaults {
  const defaults: PptxParagraphStyleDefaults = {};

  const algn = attr(pPr, 'algn');
  if (algn) defaults.alignment = algn;

  const indent = attr(pPr, 'indent');
  const marL = attr(pPr, 'marL');
  if (indent || marL) {
    const indentPx = indent ? emuToPx(parseInt(indent, 10)) : undefined;
    defaults.indent = {
      left: marL ? emuToPx(parseInt(marL, 10)) : undefined,
      // Explicit indent="0" clears both directions; undefined would inherit
      // the master body-list hanging indent into a centered subtitle.
      hanging: indentPx !== undefined ? Math.max(0, -indentPx) : undefined,
      firstLine: indentPx !== undefined ? Math.max(0, indentPx) : undefined,
    };
  }

  const buNone = getEl(pPr, A_NS, 'buNone');
  const buChar = getEl(pPr, A_NS, 'buChar');
  const buAutoNum = getEl(pPr, A_NS, 'buAutoNum');
  const buFont = getEl(pPr, A_NS, 'buFont');
  if (buNone) {
    defaults.bulletType = 'none';
  } else if (buChar) {
    defaults.bulletType = 'char';
    defaults.bulletChar = attr(buChar, 'char') || '•';
  } else if (buAutoNum) {
    defaults.bulletType = 'autoNum';
    defaults.bulletAutoNumType = attr(buAutoNum, 'type') || 'arabicPeriod';
  }
  if (buFont) {
    const typeface = resolveThemeTypeface(attr(buFont, 'typeface'), theme);
    if (typeface) defaults.bulletFontFamily = typeface;
  }
  // 불릿 색 — buClr. 미지정 시 텍스트 색을 따른다(buClrTx 동등).
  // 파싱하지 않으면 불릿이 컨테이너 색을 상속해 어두운 배경에서 검정 불릿이
  // 사라지는 사고가 난다(BayesianOpt.pptx 실측: buClr=lt1 흰 불릿).
  const buClr = getEl(pPr, A_NS, 'buClr');
  if (buClr) {
    const c = parseColor(buClr, theme);
    if (c) defaults.bulletColor = c;
  }
  // 불릿 크기 — buSzPts(1/100 pt 절대값) 또는 buSzPct(1/1000 % — 첫 런 대비).
  // PowerPoint/LO 에서 불릿 크기는 "행간에 영향을 주지 않고" 글리프만 커진다.
  const buSzPts = getEl(pPr, A_NS, 'buSzPts');
  const buSzPct = getEl(pPr, A_NS, 'buSzPct');
  if (buSzPts) {
    const v = parseInt(attr(buSzPts, 'val') || '0', 10);
    if (v > 0) defaults.bulletSizePts = v / 100;
  } else if (buSzPct) {
    const v = parseInt(attr(buSzPct, 'val') || '0', 10);
    if (v > 0) defaults.bulletSizePct = v / 100000;
  }

  const spcBef = getEl(pPr, A_NS, 'spcBef');
  const spcAft = getEl(pPr, A_NS, 'spcAft');
  const lnSpc = getEl(pPr, A_NS, 'lnSpc');
  if (spcBef || spcAft || lnSpc) {
    defaults.spacing = {};
    if (spcBef) {
      const pts = getEl(spcBef, A_NS, 'spcPts');
      if (pts) defaults.spacing.before = parseInt(attr(pts, 'val') || '0', 10) / 100;
    }
    if (spcAft) {
      const pts = getEl(spcAft, A_NS, 'spcPts');
      if (pts) defaults.spacing.after = parseInt(attr(pts, 'val') || '0', 10) / 100;
    }
    if (lnSpc) {
      const pct = getEl(lnSpc, A_NS, 'spcPct');
      const pts = getEl(lnSpc, A_NS, 'spcPts');
      if (pct) {
        defaults.spacing.lineSpacing = parseInt(attr(pct, 'val') || '100000', 10) / 1000;
      } else if (pts) {
        defaults.spacing.lineSpacing = -(parseInt(attr(pts, 'val') || '0', 10) / 100);
      }
    }
  }

  const defRPr = getEl(pPr, A_NS, 'defRPr');
  if (defRPr) {
    defaults.defaultRunProps = parseDefRPr(defRPr, theme);
  }

  return defaults;
}

function mergeParagraphStyleDefaults(
  primary?: PptxParagraphStyleDefaults,
  fallback?: PptxParagraphStyleDefaults,
): PptxParagraphStyleDefaults | undefined {
  if (!primary && !fallback) return undefined;
  const spacing = {
    before: primary?.spacing?.before ?? fallback?.spacing?.before,
    after: primary?.spacing?.after ?? fallback?.spacing?.after,
    lineSpacing: primary?.spacing?.lineSpacing ?? fallback?.spacing?.lineSpacing,
  };
  const indent = {
    left: primary?.indent?.left ?? fallback?.indent?.left,
    hanging: primary?.indent?.hanging ?? fallback?.indent?.hanging,
    firstLine: primary?.indent?.firstLine ?? fallback?.indent?.firstLine,
  };
  return {
    alignment: primary?.alignment ?? fallback?.alignment,
    bulletType: primary?.bulletType ?? fallback?.bulletType,
    bulletChar: primary?.bulletChar ?? fallback?.bulletChar,
    bulletAutoNumType: primary?.bulletAutoNumType ?? fallback?.bulletAutoNumType,
    bulletFontFamily: primary?.bulletFontFamily ?? fallback?.bulletFontFamily,
    bulletColor: primary?.bulletColor ?? fallback?.bulletColor,
    bulletSizePts: primary?.bulletSizePts ?? fallback?.bulletSizePts,
    bulletSizePct: primary?.bulletSizePct ?? fallback?.bulletSizePct,
    spacing: Object.values(spacing).some((value) => value !== undefined) ? spacing : undefined,
    indent: Object.values(indent).some((value) => value !== undefined) ? indent : undefined,
    defaultRunProps: mergeParagraphDefaults(primary?.defaultRunProps, fallback?.defaultRunProps),
  };
}

function parseListStyleDefaults(
  txBodyEl: Element,
  theme: PptxTheme,
): { defaultProps?: PptxParagraphStyleDefaults; levels: Map<number, PptxParagraphStyleDefaults> } {
  const levels = new Map<number, PptxParagraphStyleDefaults>();
  let defaultProps: PptxParagraphStyleDefaults | undefined;

  const lstStyle = getEl(txBodyEl, A_NS, 'lstStyle');
  if (!lstStyle) return { defaultProps, levels };

  const defPPr = getEl(lstStyle, A_NS, 'defPPr');
  if (defPPr) {
    defaultProps = parseParagraphStyleDefaultsFromPPr(defPPr, theme);
  }

  for (let lvl = 1; lvl <= 9; lvl++) {
    const lvlPPr = getEl(lstStyle, A_NS, `lvl${lvl}pPr`);
    if (!lvlPPr) continue;
    levels.set(lvl - 1, parseParagraphStyleDefaultsFromPPr(lvlPPr, theme));
  }

  return { defaultProps, levels };
}

function parseTextBody(
  txBodyEl: Element,
  theme: PptxTheme,
  inheritedDefaults?: PptxParagraphDefaults,
): PptxTextBody {
  const bodyPrEl = getEl(txBodyEl, A_NS, 'bodyPr');
  const bodyProps: PptxBodyProps = {};
  if (bodyPrEl) {
    bodyProps.anchor = attr(bodyPrEl, 'anchor') || undefined;
    bodyProps.anchorCtr = attr(bodyPrEl, 'anchorCtr') === '1' || attr(bodyPrEl, 'anchorCtr') === 'true';
    bodyProps.wrap = attr(bodyPrEl, 'wrap') || undefined;
    bodyProps.horzOverflow = attr(bodyPrEl, 'horzOverflow') || undefined;
    bodyProps.vertOverflow = attr(bodyPrEl, 'vertOverflow') || undefined;
    const lIns = attr(bodyPrEl, 'lIns');
    const tIns = attr(bodyPrEl, 'tIns');
    const rIns = attr(bodyPrEl, 'rIns');
    const bIns = attr(bodyPrEl, 'bIns');
    if (lIns) bodyProps.lIns = emuToPx(parseInt(lIns, 10));
    if (tIns) bodyProps.tIns = emuToPx(parseInt(tIns, 10));
    if (rIns) bodyProps.rIns = emuToPx(parseInt(rIns, 10));
    if (bIns) bodyProps.bIns = emuToPx(parseInt(bIns, 10));

    const normAutofit = getEl(bodyPrEl, A_NS, 'normAutofit');
    const spAutoFit = getEl(bodyPrEl, A_NS, 'spAutoFit');
    if (normAutofit) {
      bodyProps.autofit = 'shrink';
      // fontScale/lnSpcReduction 은 1000분의 1 % (100000 = 100%) → 0~1 소수
      const fontScale = attr(normAutofit, 'fontScale');
      if (fontScale) bodyProps.autofitFontScale = parseInt(fontScale, 10) / 100000;
      const lnSpcReduction = attr(normAutofit, 'lnSpcReduction');
      if (lnSpcReduction) bodyProps.autofitLnSpcReduction = parseInt(lnSpcReduction, 10) / 100000;
    } else if (spAutoFit) bodyProps.autofit = 'normal';
    else bodyProps.autofit = 'none';
  }

  const paragraphs: PptxParagraph[] = [];
  const listStyleDefaults = parseListStyleDefaults(txBodyEl, theme);
  const pEls = directChildrenNS(txBodyEl, A_NS, 'p');
  for (const pEl of pEls) {
    const para = parseParagraph(pEl, theme);
    const lvl = para.level ?? 0;
    const listDefaults = mergeParagraphStyleDefaults(
      listStyleDefaults.levels.get(lvl),
      listStyleDefaults.defaultProps,
    );
    mergeParagraphFormatting(para, listDefaults);
    para.defaultRunProps = mergeParagraphDefaults(para.defaultRunProps, inheritedDefaults);
    paragraphs.push(para);
  }

  return { paragraphs, bodyProps };
}

function parseDefRPr(defRPrEl: Element, theme: PptxTheme): PptxParagraphDefaults {
  const defaults: PptxParagraphDefaults = {};
  const sz = attr(defRPrEl, 'sz');
  if (sz) defaults.fontSize = parseInt(sz, 10) / 100;
  const b = attr(defRPrEl, 'b');
  if (b === '1' || b === 'true') defaults.bold = true;
  else if (b === '0' || b === 'false') defaults.bold = false;
  const i = attr(defRPrEl, 'i');
  if (i === '1' || i === 'true') defaults.italic = true;
  else if (i === '0' || i === 'false') defaults.italic = false;
  const solidFill = directChildrenNS(defRPrEl, A_NS, 'solidFill')[0];
  if (solidFill) defaults.color = parseColor(solidFill, theme);
  const latin = getEl(defRPrEl, A_NS, 'latin');
  if (latin) {
    const typeface = resolveThemeTypeface(attr(latin, 'typeface'), theme);
    if (typeface) defaults.fontFamily = typeface;
  }
  const ea = getEl(defRPrEl, A_NS, 'ea');
  if (ea) {
    const typeface = resolveThemeTypeface(attr(ea, 'typeface'), theme);
    if (typeface) defaults.eaFontFamily = typeface;
  }
  const cs = getEl(defRPrEl, A_NS, 'cs');
  if (cs) {
    const typeface = resolveThemeTypeface(attr(cs, 'typeface'), theme);
    if (typeface) defaults.csFontFamily = typeface;
  }
  return defaults;
}

function parseParagraph(pEl: Element, theme: PptxTheme): PptxParagraph {
  const para: PptxParagraph = { id: genId(), runs: [] };

  const pPr = getEl(pEl, A_NS, 'pPr');
  if (pPr) {
    const lvl = attr(pPr, 'lvl');
    if (lvl) para.level = parseInt(lvl, 10);
    mergeParagraphFormatting(para, parseParagraphStyleDefaultsFromPPr(pPr, theme));
  }

  const endParaRPr = getEl(pEl, A_NS, 'endParaRPr');
  if (endParaRPr) {
    para.defaultRunProps = mergeParagraphDefaults(
      para.defaultRunProps,
      parseDefRPr(endParaRPr, theme),
    );
  }

  for (let i = 0; i < pEl.childNodes.length; i++) {
    const child = pEl.childNodes[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;

    if (el.localName === 'r' && el.namespaceURI === A_NS) {
      para.runs.push(parseRun(el, theme));
    } else if (el.localName === 'br' && el.namespaceURI === A_NS) {
      para.runs.push({ id: genId(), type: 'break' });
    } else if (el.localName === 'fld' && el.namespaceURI === A_NS) {
      const t = getEl(el, A_NS, 't');
      // fld 의 rPr 도 일반 런처럼 해석해야 크기/색이 유지된다(parseRun 은
      // rPr/t 자식을 읽으므로 a:fld 요소에도 그대로 동작).
      // fieldType 은 파싱 후 슬라이드 번호 치환 패스(resolveSlideNumberFields)가
      // 소비한다 — 캐시 텍스트('‹#›')를 그대로 그리면 페이지 번호가 깨진다.
      const fldRun = parseRun(el, theme);
      para.runs.push({
        ...fldRun,
        type: 'field',
        fieldType: attr(el, 'type') || undefined,
        text: t?.textContent || '',
      });
    }
  }

  return para;
}

function parseRun(rEl: Element, theme: PptxTheme): PptxRun {
  const run: PptxRun = { id: genId(), type: 'text' };

  const rPr = getEl(rEl, A_NS, 'rPr');
  if (rPr) {
    const b = attr(rPr, 'b');
    if (b === '1' || b === 'true') run.bold = true;
    else if (b === '0' || b === 'false') run.bold = false;

    const i = attr(rPr, 'i');
    if (i === '1' || i === 'true') run.italic = true;
    else if (i === '0' || i === 'false') run.italic = false;

    const u = attr(rPr, 'u');
    if (u) run.underline = u !== 'none';

    const strike = attr(rPr, 'strike');
    if (strike) run.strike = strike !== 'noStrike';

    // baseline: 위첨자(양수)/아래첨자(음수), 1000분의 1% 단위(30000 = 30%).
    const baseline = attr(rPr, 'baseline');
    if (baseline) {
      const bl = parseInt(baseline, 10);
      if (!isNaN(bl)) run.baseline = bl;
    }

    const sz = attr(rPr, 'sz');
    if (sz) run.fontSize = parseInt(sz, 10) / 100;

    // directChildrenNS 사용: a:ln 등 자식 엘리먼트 내부의 solidFill 오인식 방지
    const solidFill = directChildrenNS(rPr, A_NS, 'solidFill')[0];
    if (solidFill) run.color = parseColor(solidFill, theme);

    const latin = getEl(rPr, A_NS, 'latin');
    if (latin) {
      const typeface = resolveThemeTypeface(attr(latin, 'typeface'), theme);
      if (typeface) run.fontFamily = typeface;
    }

    const ea = getEl(rPr, A_NS, 'ea');
    if (ea) {
      const typeface = resolveThemeTypeface(attr(ea, 'typeface'), theme);
      if (typeface) {
        run.eaFontFamily = typeface;
        // latin 미지정 런은 ea 폰트를 대표 폰트로 사용 (기존 동작 유지)
        if (!run.fontFamily) run.fontFamily = typeface;
      }
    }

    const cs = getEl(rPr, A_NS, 'cs');
    if (cs) {
      const typeface = resolveThemeTypeface(attr(cs, 'typeface'), theme);
      if (typeface) run.csFontFamily = typeface;
    }

    const hlinkClr = getEl(rPr, A_NS, 'highlight');
    if (hlinkClr) run.highlight = parseColor(hlinkClr, theme);
  }

  const t = getEl(rEl, A_NS, 't');
  if (t) run.text = t.textContent || '';

  return run;
}

/* ------------------------------------------------------------------ */
/* 도형(Shape) 파싱                                                     */
/* ------------------------------------------------------------------ */

function parseTransform(spPrEl: Element): {
  pos: { x: number; y: number };
  size: { width: number; height: number };
  rotation?: number;
  flipH?: boolean;
  flipV?: boolean;
} | null {
  const xfrm = getEl(spPrEl, A_NS, 'xfrm');
  if (!xfrm) return null;

  const off = getEl(xfrm, A_NS, 'off');
  const ext = getEl(xfrm, A_NS, 'ext');
  if (!off || !ext) return null;

  const x = parseInt(attr(off, 'x') || '0', 10);
  const y = parseInt(attr(off, 'y') || '0', 10);
  const cx = parseInt(attr(ext, 'cx') || '0', 10);
  const cy = parseInt(attr(ext, 'cy') || '0', 10);

  const rot = attr(xfrm, 'rot');
  const rotation = rot ? parseInt(rot, 10) / 60000 : undefined;

  const flipHVal = attr(xfrm, 'flipH');
  const flipVVal = attr(xfrm, 'flipV');
  const flipH = flipHVal === '1' || flipHVal === 'true' ? true : undefined;
  const flipV = flipVVal === '1' || flipVVal === 'true' ? true : undefined;

  return {
    pos: { x: emuToPx(x), y: emuToPx(y) },
    size: { width: emuToPx(cx), height: emuToPx(cy) },
    rotation,
    flipH,
    flipV,
  };
}

function parseGeometry(spPrEl: Element): PptxGeometry | undefined {
  const prstGeom = getEl(spPrEl, A_NS, 'prstGeom');
  if (prstGeom) {
    // avLst 조정값 — pie/arc 등 각도·비율 파라미터를 갖는 preset 용.
    // fmla="val N" 형태만 해석한다(가이드 수식 참조는 렌더러가 기본값 폴백).
    let adjustments: Record<string, number> | undefined;
    const avLst = getEl(prstGeom, A_NS, 'avLst');
    if (avLst) {
      for (let i = 0; i < avLst.childNodes.length; i++) {
        const gd = avLst.childNodes[i];
        if (gd.nodeType !== 1 || (gd as Element).localName !== 'gd') continue;
        const name = attr(gd as Element, 'name');
        const fmla = attr(gd as Element, 'fmla');
        const m = fmla?.match(/^val (-?\d+)$/);
        if (name && m) {
          adjustments = adjustments ?? {};
          adjustments[name] = parseInt(m[1], 10);
        }
      }
    }
    return { preset: attr(prstGeom, 'prst') || 'rect', adjustments };
  }
  const custGeom = getEl(spPrEl, A_NS, 'custGeom');
  if (custGeom) {
    return { preset: 'custom', custom: parseCustomGeometry(custGeom, spPrEl) };
  }
  return undefined;
}

/** 소수 노이즈 없는 SVG 좌표 문자열 (정수는 그대로, 소수는 2자리) */
function fmtCoord(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

/**
 * a:custGeom/a:pathLst → PptxCustomGeomPath[].
 * - 좌표는 path 좌표 공간(w×h) 단위 그대로 두고 렌더러가 도형 박스로 스케일.
 * - w/h 속성 부재 시 좌표 공간은 도형 크기(EMU) — spPr 의 xfrm ext 로 폴백
 *   (실덱 test_02 에 <a:path w="..."> 만 있고 h 없는 경로가 실존).
 * - 좌표가 가이드 참조(비숫자)인 경로는 변환 불가로 스킵 (preset='custom' 폴백 유지).
 */
/**
 * a:gdLst 가이드 수식 평가 — 좌표가 가이드 이름을 참조하는 custGeom
 * (PowerPoint/일부 도구가 생성)을 지원한다. OOXML 수식 중 실덱 빈도가 높은
 * 부분집합만 구현: val, * / (a*b/c), +- (a+b-c), +/ ((a+b)/c), abs, min, max,
 * pin(clamp), ?: (a>0?b:c). 미지원 수식을 만난 가이드는 정의하지 않는다
 * (해당 가이드를 참조하는 경로만 폴백).
 */
function evaluateGeomGuides(
  custGeomEl: Element,
  shapeW: number,
  shapeH: number,
): Map<string, number> {
  const g = new Map<string, number>();
  // 내장 변수 (OOXML 사양의 사전 정의 가이드)
  const ss = Math.min(shapeW, shapeH);
  g.set('w', shapeW); g.set('h', shapeH); g.set('ss', ss);
  g.set('l', 0); g.set('t', 0); g.set('r', shapeW); g.set('b', shapeH);
  g.set('wd2', shapeW / 2); g.set('hd2', shapeH / 2); g.set('ssd2', ss / 2);
  g.set('wd4', shapeW / 4); g.set('hd4', shapeH / 4); g.set('ssd4', ss / 4);
  g.set('wd8', shapeW / 8); g.set('hd8', shapeH / 8); g.set('ssd8', ss / 8);
  g.set('hc', shapeW / 2); g.set('vc', shapeH / 2);
  g.set('cd2', 10800000); g.set('cd4', 5400000); g.set('cd8', 2700000);
  g.set('3cd4', 16200000); g.set('3cd8', 8100000); g.set('5cd8', 13500000);
  g.set('7cd8', 18900000);

  const resolve = (token: string): number | undefined => {
    const n = Number(token);
    if (Number.isFinite(n)) return n;
    return g.get(token);
  };

  const gdLst = getEl(custGeomEl, A_NS, 'gdLst');
  if (!gdLst) return g;
  for (const gd of directChildrenNS(gdLst, A_NS, 'gd')) {
    const name = attr(gd, 'name');
    const fmla = attr(gd, 'fmla');
    if (!name || !fmla) continue;
    const parts = fmla.split(/\s+/);
    const op = parts[0];
    const args = parts.slice(1).map(resolve);
    if (args.some((a) => a === undefined)) continue;
    const [a, b, c] = args as number[];
    let value: number | undefined;
    switch (op) {
      case 'val': value = a; break;
      case '*/': value = c !== 0 ? (a * b) / c : 0; break;
      case '+-': value = a + b - (c ?? 0); break;
      case '+/': value = c !== 0 ? (a + b) / c : 0; break;
      case 'abs': value = Math.abs(a); break;
      case 'min': value = Math.min(a, b); break;
      case 'max': value = Math.max(a, b); break;
      case 'pin': value = Math.max(a, Math.min(b, c)); break;
      case '?:': value = a > 0 ? b : c; break;
      default: value = undefined;
    }
    if (value !== undefined && Number.isFinite(value)) g.set(name, value);
  }
  return g;
}

function parseCustomGeometry(custGeomEl: Element, spPrEl: Element): PptxCustomGeomPath[] | undefined {
  const pathLst = getEl(custGeomEl, A_NS, 'pathLst');
  if (!pathLst) return undefined;

  const xfrm = getEl(spPrEl, A_NS, 'xfrm');
  const ext = xfrm ? getEl(xfrm, A_NS, 'ext') : null;
  const extCx = ext ? parseInt(attr(ext, 'cx') || '0', 10) : 0;
  const extCy = ext ? parseInt(attr(ext, 'cy') || '0', 10) : 0;

  const result: PptxCustomGeomPath[] = [];
  for (const pathEl of directChildrenNS(pathLst, A_NS, 'path')) {
    // 가이드는 "경로 좌표 공간"이 아니라 도형(EMU) 공간에서 정의된다.
    // 경로별 w/h 가 없으면 좌표 공간 == 도형 공간이므로 동일 값으로 평가.
    const pathW = attr(pathEl, 'w');
    const pathH = attr(pathEl, 'h');
    const guides = evaluateGeomGuides(
      custGeomEl,
      pathW ? parseInt(pathW, 10) : extCx,
      pathH ? parseInt(pathH, 10) : extCy,
    );
    const d = convertCustGeomPath(pathEl, guides);
    if (!d) continue;

    const wAttr = attr(pathEl, 'w');
    const hAttr = attr(pathEl, 'h');
    const strokeAttr = attr(pathEl, 'stroke');
    result.push({
      d,
      w: wAttr ? parseInt(wAttr, 10) : extCx,
      h: hAttr ? parseInt(hAttr, 10) : extCy,
      filled: attr(pathEl, 'fill') !== 'none',
      stroked: !(strokeAttr === '0' || strokeAttr === 'false'),
    });
  }
  return result.length > 0 ? result : undefined;
}

/** a:path 자식 명령(moveTo/lnTo/cubicBezTo/quadBezTo/arcTo/close)을 SVG path data 로 변환 */
function convertCustGeomPath(pathEl: Element, guides?: Map<string, number>): string | null {
  const readCoord = (raw: string | null): number | null => {
    if (!raw) return null;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
    // 가이드 이름 참조 좌표 — gdLst 평가 결과에서 해석 (미해석이면 경로 폴백)
    const fromGuide = guides?.get(raw);
    return fromGuide !== undefined && Number.isFinite(fromGuide) ? fromGuide : null;
  };
  const readPt = (ptEl: Element | undefined): { x: number; y: number } | null => {
    if (!ptEl) return null;
    const x = readCoord(attr(ptEl, 'x'));
    const y = readCoord(attr(ptEl, 'y'));
    if (x === null || y === null) return null;
    return { x, y };
  };

  const segments: string[] = [];
  let cur = { x: 0, y: 0 };
  let subpathStart = { x: 0, y: 0 };

  for (let i = 0; i < pathEl.childNodes.length; i++) {
    const node = pathEl.childNodes[i];
    if (node.nodeType !== 1) continue;
    const el = node as Element;
    if (el.namespaceURI !== A_NS) continue;

    const pts = directChildrenNS(el, A_NS, 'pt');
    switch (el.localName) {
      case 'moveTo': {
        const pt = readPt(pts[0]);
        if (!pt) return null;
        segments.push(`M ${fmtCoord(pt.x)} ${fmtCoord(pt.y)}`);
        cur = pt;
        subpathStart = pt;
        break;
      }
      case 'lnTo': {
        const pt = readPt(pts[0]);
        if (!pt) return null;
        segments.push(`L ${fmtCoord(pt.x)} ${fmtCoord(pt.y)}`);
        cur = pt;
        break;
      }
      case 'cubicBezTo': {
        const p1 = readPt(pts[0]);
        const p2 = readPt(pts[1]);
        const p3 = readPt(pts[2]);
        if (!p1 || !p2 || !p3) return null;
        segments.push(
          `C ${fmtCoord(p1.x)} ${fmtCoord(p1.y)} ${fmtCoord(p2.x)} ${fmtCoord(p2.y)} ${fmtCoord(p3.x)} ${fmtCoord(p3.y)}`,
        );
        cur = p3;
        break;
      }
      case 'quadBezTo': {
        const p1 = readPt(pts[0]);
        const p2 = readPt(pts[1]);
        if (!p1 || !p2) return null;
        segments.push(`Q ${fmtCoord(p1.x)} ${fmtCoord(p1.y)} ${fmtCoord(p2.x)} ${fmtCoord(p2.y)}`);
        cur = p2;
        break;
      }
      case 'arcTo': {
        // 반경/각도도 가이드 참조가 가능하다 (readCoord 로 동일 해석)
        const wR = readCoord(attr(el, 'wR'));
        const hR = readCoord(attr(el, 'hR'));
        const stAng = readCoord(attr(el, 'stAng'));
        const swAng = readCoord(attr(el, 'swAng'));
        if (wR === null || hR === null || stAng === null || swAng === null) {
          return null;
        }
        const arc = convertArcTo(cur, wR, hR, stAng, swAng);
        segments.push(arc.d);
        cur = arc.end;
        break;
      }
      case 'close': {
        segments.push('Z');
        cur = subpathStart;
        break;
      }
      default:
        break;
    }
  }

  return segments.length > 0 ? segments.join(' ') : null;
}

/**
 * a:arcTo → SVG A 세그먼트.
 * OOXML arcTo 는 현재점이 (wR,hR) 타원의 stAng 위치에 놓인다는 정의 —
 * 타원 중심을 역산해 stAng+swAng 위치를 끝점으로 삼는다 (각도는 1/60000도,
 * 파라메트릭 근사 — 90도 배수는 정확). |swAng| ≥ 180도는 SVG large-arc 만으로
 * 모호해질 수 있어 180도 이하 조각으로 분할한다 (360도 전체 원도 안전).
 */
function convertArcTo(
  cur: { x: number; y: number },
  wR: number,
  hR: number,
  stAng: number,
  swAng: number,
): { d: string; end: { x: number; y: number } } {
  const toRad = (a: number) => ((a / 60000) * Math.PI) / 180;
  const st = toRad(stAng);
  const sw = toRad(swAng);
  const ptOnEllipse = (t: number) => ({ x: wR * Math.cos(t), y: hR * Math.sin(t) });

  const startRel = ptOnEllipse(st);
  const center = { x: cur.x - startRel.x, y: cur.y - startRel.y };
  const sweepFlag = sw >= 0 ? 1 : 0;

  const segCount = Math.max(1, Math.ceil(Math.abs(sw) / Math.PI));
  const parts: string[] = [];
  let end = cur;
  for (let seg = 1; seg <= segCount; seg++) {
    const t = st + (sw * seg) / segCount;
    const rel = ptOnEllipse(t);
    end = { x: center.x + rel.x, y: center.y + rel.y };
    parts.push(`A ${fmtCoord(wR)} ${fmtCoord(hR)} 0 0 ${sweepFlag} ${fmtCoord(end.x)} ${fmtCoord(end.y)}`);
  }
  return { d: parts.join(' '), end };
}

interface PptxShapeStyleDefaults {
  textDefaults?: PptxParagraphDefaults;
  fill?: PptxFill;
  outline?: PptxOutline;
  shadow?: PptxShadow;
}

function parseShapeStyleDefaults(spEl: Element, theme: PptxTheme): PptxShapeStyleDefaults | undefined {
  const styleEl = getEl(spEl, P_NS, 'style');
  if (!styleEl) return undefined;

  const fontRef = getEl(styleEl, A_NS, 'fontRef');
  const styleDefaults: PptxShapeStyleDefaults = {};

  if (fontRef) {
    const defaults: PptxParagraphDefaults = {};
    const idx = attr(fontRef, 'idx');
    if (idx === 'major') defaults.fontFamily = theme.fonts.major;
    if (idx === 'minor') defaults.fontFamily = theme.fonts.minor;

    const color = parseColor(fontRef, theme);
    if (color) defaults.color = color;

    if (Object.keys(defaults).length > 0) {
      styleDefaults.textDefaults = defaults;
    }
  }

  const fillRef = getEl(styleEl, A_NS, 'fillRef');
  if (fillRef) {
    // 테마 fmtScheme(fillStyleLst/bgFillStyleLst) 해석 — phClr 는 참조 색으로 치환.
    // fmtScheme 이 없으면 기존 동작(참조 색 solid)으로 폴백한다.
    const fill = resolveStyleRefFill(fillRef, theme);
    if (fill && fill.type !== 'none') {
      styleDefaults.fill = fill;
    }
  }

  const lnRef = getEl(styleEl, A_NS, 'lnRef');
  if (lnRef) {
    const idx = parseInt(attr(lnRef, 'idx') || '1', 10);
    const outline: PptxOutline = idx > 0 && idx <= theme.lineStyles.length
      ? { ...theme.lineStyles[idx - 1] }
      : {};
    const color = parseColor(lnRef, theme);
    if (color) outline.color = color;
    if (!outline.width && outline.color) outline.width = 1;
    if (outline.width || outline.color || outline.dashStyle) {
      styleDefaults.outline = outline;
    }
  }

  const effectRef = getEl(styleEl, A_NS, 'effectRef');
  if (effectRef) {
    const shadow = resolveStyleRefShadow(effectRef, theme);
    if (shadow) styleDefaults.shadow = shadow;
  }

  return Object.keys(styleDefaults).length > 0 ? styleDefaults : undefined;
}

/** cNvPr@id 를 순수 숫자 앵커로 변환. 비숫자(genId 폴백)면 null(anchorless). */
function toCNvId(cNvPr: Element | null): number | null {
  const raw = cNvPr ? attr(cNvPr, 'id') : null;
  return raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : null;
}

function parseShape(spEl: Element, theme: PptxTheme): PptxShape | null {
  const nvSpPr = getEl(spEl, P_NS, 'nvSpPr') || getEl(spEl, P_NS, 'nvCxnSpPr');
  const cNvPr = nvSpPr ? getEl(nvSpPr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  const spPr = getEl(spEl, P_NS, 'spPr');
  if (!spPr) return null;

  let placeholder: PptxShape['placeholder'];
  if (nvSpPr) {
    const nvPr = getEl(nvSpPr, P_NS, 'nvPr');
    if (nvPr) {
      const ph = getEl(nvPr, P_NS, 'ph');
      if (ph) {
        placeholder = {
          type: attr(ph, 'type') || 'body',
          idx: attr(ph, 'idx') || undefined,
        };
      }
    }
  }

  const transform = parseTransform(spPr);
  // Placeholders without explicit xfrm inherit from layout; use a default
  if (!transform && !placeholder) return null;

  const pos = transform?.pos ?? { x: 0, y: 0 };
  const size = transform?.size ?? { width: 0, height: 0 };
  const rotation = transform?.rotation;
  const flipH = transform?.flipH;
  const flipV = transform?.flipV;

  const isConnector = spEl.localName === 'cxnSp';
  const hasTextBody = !!getEl(spEl, P_NS, 'txBody');
  const geometry = parseGeometry(spPr);
  const hasGeometry = !!geometry && geometry.preset !== 'rect';
  const styleDefaults = parseShapeStyleDefaults(spEl, theme);

  const fillVal = parseFill(spPr, theme);
  const lnEl = getEl(spPr, A_NS, 'ln');
  // 명시적 <a:ln><a:noFill/> 은 "윤곽선 없음" 확정이다 — p:style 의 lnRef 로
  // 폴백하면 안 된다(폴백 시 투명 텍스트박스에 테마 테두리가 생기는 사고 —
  // sm_klcsm.pptx 제목 배너 실측). ln 요소 자체가 없을 때만 스타일 참조를 쓴다.
  const lnExplicitNone = !!(lnEl && getEl(lnEl, A_NS, 'noFill'));
  const outlineVal = lnEl ? parseOutline(lnEl, theme) : undefined;
  const effectiveFill = fillVal ?? styleDefaults?.fill;
  const effectiveOutline = lnExplicitNone
    ? undefined
    : (outlineVal ?? styleDefaults?.outline);
  const hasVisualDecoration = !!(effectiveFill && effectiveFill.type !== 'none') || !!effectiveOutline;

  let type: PptxShape['type'] = 'shape';
  if (isConnector) type = 'connector';
  else if (hasTextBody && !hasGeometry && !placeholder && !hasVisualDecoration) type = 'textbox';
  else if (hasTextBody || hasGeometry || placeholder || hasVisualDecoration) type = 'shape';

  const shape: PptxShape = {
    id: `sp_${shapeId}`,
    cNvId: toCNvId(cNvPr),
    name: shapeName,
    type,
    position: pos,
    size,
    hasExplicitTransform: !!transform,
    rotation,
    flipH,
    flipV,
    geometry: geometry ?? { preset: 'rect' },
    placeholder,
  };

  shape.fill = effectiveFill;
  shape.outline = effectiveOutline;

  // 그림자: spPr 의 a:effectLst 가 존재하면(비어 있어도) 명시 오버라이드 —
  // outerShdw 없는 빈 effectLst 는 스타일(effectRef) 그림자를 끄는 의미다.
  const effectLstEl = directChildrenNS(spPr, A_NS, 'effectLst')[0];
  shape.shadow = effectLstEl
    ? parseOuterShadow(effectLstEl, theme)
    : styleDefaults?.shadow;

  const txBody = getEl(spEl, P_NS, 'txBody');
  if (txBody) {
    const inheritedDefaults = styleDefaults?.textDefaults;
    shape.textBody = parseTextBody(txBody, theme, inheritedDefaults);
  }

  // 커넥터 글루 — p:cNvCxnSpPr > a:stCxn/a:endCxn(A_NS). id=대상 cNvPr@id(숫자),
  // idx=연결 사이트 인덱스. 파싱해 모델에 보존(도형 이동 시 재글루 + 저장 되쓰기).
  if (isConnector && nvSpPr) {
    const cNvCxnSpPr = getEl(nvSpPr, P_NS, 'cNvCxnSpPr');
    if (cNvCxnSpPr) {
      const readCxn = (name: 'stCxn' | 'endCxn') => {
        const el = getEl(cNvCxnSpPr, A_NS, name);
        if (!el) return undefined;
        const idRaw = attr(el, 'id'); const idxRaw = attr(el, 'idx');
        // id/idx 둘 다 숫자여야 유효(비정상 값은 NaN 되쓰기 방지 위해 드롭).
        if (!idRaw || !/^\d+$/.test(idRaw) || !idxRaw || !/^\d+$/.test(idxRaw)) return undefined;
        return { targetCNvId: parseInt(idRaw, 10), idx: parseInt(idxRaw, 10) };
      };
      const st = readCxn('stCxn'); const en = readCxn('endCxn');
      if (st) shape.startConnection = st;
      if (en) shape.endConnection = en;
    }
  }

  return shape;
}

function parsePicture(picEl: Element, theme: PptxTheme): PptxShape | null {
  const nvPicPr = getEl(picEl, P_NS, 'nvPicPr');
  const cNvPr = nvPicPr ? getEl(nvPicPr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  const spPr = getEl(picEl, P_NS, 'spPr');
  if (!spPr) return null;

  // placeholder 그림은 xfrm 없이 레이아웃/마스터에서 위치·크기를 상속한다
  // (resolvePlaceholderTransforms). 종전에는 무조건 드롭돼 레이아웃 배치형
  // 이미지(startup_pitchdeck 전구 실측)가 사라졌다.
  const nvPrEarly = nvPicPr ? getEl(nvPicPr, P_NS, 'nvPr') : null;
  const phElEarly = nvPrEarly ? getEl(nvPrEarly, P_NS, 'ph') : null;
  const transform = parseTransform(spPr);
  if (!transform && !phElEarly) return null;

  const blipFill = getEl(picEl, P_NS, 'blipFill');
  let imageRId: string | undefined;
  let imageCrop: PptxImageCrop | undefined;
  if (blipFill) {
    // parseFill 의 그림 채움 경로와 동일한 blip/srcRect 해석 공용 헬퍼 재사용
    const blipRef = parseBlipRef(blipFill);
    imageRId = blipRef.rId;
    imageCrop = blipRef.srcRect;
  }

  // 그림 도형의 spPr effectLst (outerShdw) — 도형과 동일 계약
  const effectLstEl = directChildrenNS(spPr, A_NS, 'effectLst')[0];
  const shadow = effectLstEl ? parseOuterShadow(effectLstEl, theme) : undefined;

  // 그림 placeholder 여부 — 레이아웃 pic ph 의 지오메트리(아치 클리핑 등)
  // 상속을 위해 필요 (resolvePlaceholderTransforms 가 소비).
  let placeholder: PptxShape['placeholder'];
  const nvPr = nvPicPr ? getEl(nvPicPr, P_NS, 'nvPr') : null;
  const phEl = nvPr ? getEl(nvPr, P_NS, 'ph') : null;
  if (phEl) {
    placeholder = {
      type: attr(phEl, 'type') || 'pic',
      idx: attr(phEl, 'idx') || undefined,
    };
  }

  return {
    id: `pic_${shapeId}`,
    cNvId: toCNvId(cNvPr),
    name: shapeName,
    type: 'image',
    position: transform?.pos ?? { x: 0, y: 0 },
    size: transform?.size ?? { width: 0, height: 0 },
    hasExplicitTransform: !!transform,
    rotation: transform?.rotation,
    flipH: transform?.flipH,
    flipV: transform?.flipV,
    imageRId,
    imageCrop,
    // 그림 자체의 지오메트리(아치·라운드 클리핑) — 렌더러가 clip-path 로 소비
    geometry: parseGeometry(spPr),
    placeholder,
    fill: parseFill(spPr, theme),
    shadow,
  };
}

function parseGroupShape(grpSpEl: Element, theme: PptxTheme): PptxShape | null {
  const nvGrpSpPr = getEl(grpSpEl, P_NS, 'nvGrpSpPr');
  const cNvPr = nvGrpSpPr ? getEl(nvGrpSpPr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  const grpSpPr = getEl(grpSpEl, P_NS, 'grpSpPr');
  if (!grpSpPr) return null;

  const xfrm = getEl(grpSpPr, A_NS, 'xfrm');
  const off = xfrm ? getEl(xfrm, A_NS, 'off') : null;
  const ext = xfrm ? getEl(xfrm, A_NS, 'ext') : null;

  const children = parseShapeTree(grpSpEl, theme);

  // a:grpFill 치환 — 자식이 "그룹 채움 상속" 마커를 갖고 있으면 그룹 자체의
  // 채움(grpSpPr)으로 바꾼다. 그룹 채움도 grpFill(조부 상속)이면 상위 그룹
  // 파싱 시 재귀적으로 다시 치환된다. (roadmaps.pptx 아이콘 그룹 실측 —
  // 미치환 시 자식 도형이 전부 투명으로 렌더돼 아이콘이 사라진다.)
  const groupFill = parseFill(grpSpPr, theme);
  const substituteGrpFill = (shape: PptxShape): void => {
    if (shape.fill?.type === 'group') {
      shape.fill = groupFill && groupFill.type !== 'group' ? { ...groupFill } : undefined;
    }
    shape.children?.forEach(substituteGrpFill);
  };
  if (groupFill && groupFill.type !== 'group') {
    children.forEach(substituteGrpFill);
  }

  // 그룹 컨테이너(grpSpPr)의 effectLst — 그룹 전체 그림자
  const groupEffectLst = directChildrenNS(grpSpPr, A_NS, 'effectLst')[0];
  const groupShadow = groupEffectLst ? parseOuterShadow(groupEffectLst, theme) : undefined;

  if (xfrm && off && ext) {
    // 그룹 컨테이너 변환(rot/flipH/flipV)은 자식 재배치가 아니라 그룹 자체의
    // rotation/flipH/flipV 필드로 기록 — 렌더러가 컨테이너에 transform 적용
    const rot = attr(xfrm, 'rot');
    const flipHVal = attr(xfrm, 'flipH');
    const flipVVal = attr(xfrm, 'flipV');

    const shape: PptxShape = {
      id: `grp_${shapeId}`,
      cNvId: toCNvId(cNvPr),
      name: shapeName,
      type: 'group',
      position: { x: emuToPx(parseInt(attr(off, 'x') || '0', 10)), y: emuToPx(parseInt(attr(off, 'y') || '0', 10)) },
      size: { width: emuToPx(parseInt(attr(ext, 'cx') || '0', 10)), height: emuToPx(parseInt(attr(ext, 'cy') || '0', 10)) },
      hasExplicitTransform: true,
      rotation: rot ? parseInt(rot, 10) / 60000 : undefined,
      flipH: flipHVal === '1' || flipHVal === 'true' ? true : undefined,
      flipV: flipVVal === '1' || flipVVal === 'true' ? true : undefined,
      shadow: groupShadow,
      children,
    };

    const chOff = getEl(xfrm, A_NS, 'chOff');
    const chExt = getEl(xfrm, A_NS, 'chExt');
    if (chOff) {
      shape.groupChildOffset = {
        x: emuToPx(parseInt(attr(chOff, 'x') || '0', 10)),
        y: emuToPx(parseInt(attr(chOff, 'y') || '0', 10)),
      };
    }
    if (chExt) {
      shape.groupChildExtent = {
        width: emuToPx(parseInt(attr(chExt, 'cx') || '0', 10)),
        height: emuToPx(parseInt(attr(chExt, 'cy') || '0', 10)),
      };
    }

    return shape;
  }

  // xfrm 이 없는 그룹 — 드롭하지 않고 자식들의 바운딩박스로 off/ext 를 유도.
  // chOff/chExt 를 같은 박스로 두면 자식 좌표 변환이 항등이 되어
  // 자식이 원래 좌표 그대로 렌더된다.
  if (children.length === 0) return null;

  // 명시 transform 이 있는 자식만 박스 계산에 사용 (placeholder 상속 도형의
  // 0,0/0x0 기본값이 박스를 오염시키지 않도록). 하나도 없으면 전체 사용.
  const measurable = children.filter((child) => child.hasExplicitTransform);
  const boxSources = measurable.length > 0 ? measurable : children;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const child of boxSources) {
    minX = Math.min(minX, child.position.x);
    minY = Math.min(minY, child.position.y);
    maxX = Math.max(maxX, child.position.x + child.size.width);
    maxY = Math.max(maxY, child.position.y + child.size.height);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

  const bboxPos = { x: minX, y: minY };
  const bboxSize = { width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };

  return {
    id: `grp_${shapeId}`,
    cNvId: toCNvId(cNvPr),
    name: shapeName,
    type: 'group',
    position: bboxPos,
    size: bboxSize,
    hasExplicitTransform: false,
    shadow: groupShadow,
    children,
    groupChildOffset: { ...bboxPos },
    groupChildExtent: { ...bboxSize },
  };
}

function parseGraphicFrame(gfEl: Element, theme: PptxTheme): PptxShape | null {
  const nvGraphicFramePr = getEl(gfEl, P_NS, 'nvGraphicFramePr');
  const cNvPr = nvGraphicFramePr ? getEl(nvGraphicFramePr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  // graphicFrame uses p:xfrm (P_NS) containing a:off/a:ext (A_NS)
  const xfrm = directChildrenNS(gfEl, P_NS, 'xfrm')[0]
    || getEl(gfEl, P_NS, 'xfrm')
    || getEl(gfEl, A_NS, 'xfrm');

  let pos = { x: 0, y: 0 };
  let size = { width: 0, height: 0 };

  if (xfrm) {
    const off = getEl(xfrm, A_NS, 'off') || xfrm.querySelector('off');
    const ext = getEl(xfrm, A_NS, 'ext') || xfrm.querySelector('ext');
    if (off) {
      pos = { x: emuToPx(parseInt(attr(off, 'x') || '0', 10)), y: emuToPx(parseInt(attr(off, 'y') || '0', 10)) };
    }
    if (ext) {
      size = { width: emuToPx(parseInt(attr(ext, 'cx') || '0', 10)), height: emuToPx(parseInt(attr(ext, 'cy') || '0', 10)) };
    }
  }

  const graphic = gfEl.getElementsByTagNameNS(A_NS, 'graphic')[0];
  if (!graphic) return null;

  const graphicData = getEl(graphic, A_NS, 'graphicData');
  if (!graphicData) return null;

  const tblEl = graphicData.getElementsByTagNameNS(A_NS, 'tbl')[0];
  if (tblEl) {
    const tableGrid = parseTable(tblEl, theme);

    // PowerPoint는 실제 테이블 크기로 그리드 컬럼/행 치수를 사용함.
    // Google Slides 내보내기에서 frame cx/cy가 grid 합계와 다를 때 grid를 우선 사용.
    const gridWidth = tableGrid.gridCols.reduce((s, w) => s + w, 0);
    const gridHeight = tableGrid.rows.reduce((s, r) => s + r.height, 0);
    const actualSize = {
      width: gridWidth > 0 ? gridWidth : size.width,
      height: gridHeight > 0 ? gridHeight : size.height,
    };

    return {
      id: `tbl_${shapeId}`,
      cNvId: toCNvId(cNvPr),
      name: shapeName,
      type: 'table',
      position: pos,
      size: actualSize,
      tableGrid,
    };
  }

  // 차트 — <c:chart r:id="..."/>. 차트 파트(XML)는 별도 zip 엔트리라 여기서
  // 읽을 수 없으므로 rId 만 기록하고, parsePptx 후처리(resolveChartParts)가
  // 파트를 읽어 shape.chart 로 채운다. (종전에는 null 반환 → 차트가 캔버스에서
  // 통째로 사라지는 갭 — startup_pitchdeck 실측.)
  const chartEl = graphicData.getElementsByTagNameNS(C_NS, 'chart')[0];
  if (chartEl) {
    const chartRId = chartEl.getAttributeNS(R_NS, 'id') || undefined;
    if (chartRId && size.width > 0 && size.height > 0) {
      return {
        id: `chart_${shapeId}`,
        cNvId: toCNvId(cNvPr),
        name: shapeName,
        type: 'chart',
        position: pos,
        size,
        chartRId,
      };
    }
  }

  return null;
}

function parseTable(tblEl: Element, theme: PptxTheme): PptxTableGrid {
  // tblPr — 표 스타일 참조(tableStyleId)와 적용 플래그. 실제 스타일 해석·적용은
  // tableStyles.xml 을 읽을 수 있는 parsePptx 후처리에서 수행한다.
  let styleRef: PptxTableStyleRef | undefined;
  const tblPr = getEl(tblEl, A_NS, 'tblPr');
  if (tblPr) {
    const flag = (name: string) => {
      const v = attr(tblPr, name);
      return v === '1' || v === 'true' ? true : undefined;
    };
    const styleIdEl = getEl(tblPr, A_NS, 'tableStyleId');
    styleRef = {
      styleId: styleIdEl?.textContent?.trim() || undefined,
      firstRow: flag('firstRow'),
      lastRow: flag('lastRow'),
      bandRow: flag('bandRow'),
      firstCol: flag('firstCol'),
      lastCol: flag('lastCol'),
      bandCol: flag('bandCol'),
    };
  }

  const gridCols: number[] = [];
  const tblGrid = getEl(tblEl, A_NS, 'tblGrid');
  if (tblGrid) {
    const gridColEls = directChildrenNS(tblGrid, A_NS, 'gridCol');
    for (const gc of gridColEls) {
      const w = attr(gc, 'w');
      gridCols.push(w ? emuToPx(parseInt(w, 10)) : 100);
    }
  }

  const rows: PptxTableRow[] = [];
  const trEls = directChildrenNS(tblEl, A_NS, 'tr');
  for (let trIdx = 0; trIdx < trEls.length; trIdx++) {
    const trEl = trEls[trIdx];
    const h = attr(trEl, 'h');
    const row: PptxTableRow = {
      height: h ? emuToPx(parseInt(h, 10)) : 30,
      cells: [],
      srcIndex: trIdx,
    };

    const tcEls = directChildrenNS(trEl, A_NS, 'tc');
    for (let tcIdx = 0; tcIdx < tcEls.length; tcIdx++) {
      const tcEl = tcEls[tcIdx];
      const cell: PptxTableCell = { srcIndex: tcIdx };
      const tcPr = getEl(tcEl, A_NS, 'tcPr');

      const gridSpan = attr(tcEl, 'gridSpan') || (tcPr ? attr(tcPr, 'gridSpan') : null);
      if (gridSpan) cell.colSpan = parseInt(gridSpan, 10);

      const rowSpan = attr(tcEl, 'rowSpan') || (tcPr ? attr(tcPr, 'rowSpan') : null);
      if (rowSpan) cell.rowSpan = parseInt(rowSpan, 10);

      const hMerge = attr(tcEl, 'hMerge') || (tcPr ? attr(tcPr, 'hMerge') : null);
      if (hMerge === '1' || hMerge === 'true') cell.hMerge = true;

      const vMerge = attr(tcEl, 'vMerge') || (tcPr ? attr(tcPr, 'vMerge') : null);
      if (vMerge === '1' || vMerge === 'true') cell.vMerge = true;

      if (tcPr) {
        cell.fill = parseFill(tcPr, theme);

        // 셀 내부 패딩 (marL/R/T/B → EMU → px), 기본값 PPTX 표준 91440 EMU = ~9.6px
        const defaultPadPx = emuToPx(91440);
        const marL = attr(tcPr, 'marL');
        const marR = attr(tcPr, 'marR');
        const marT = attr(tcPr, 'marT');
        const marB = attr(tcPr, 'marB');
        cell.padding = {
          left:   marL ? emuToPx(parseInt(marL, 10)) : defaultPadPx,
          right:  marR ? emuToPx(parseInt(marR, 10)) : defaultPadPx,
          top:    marT ? emuToPx(parseInt(marT, 10)) : defaultPadPx,
          bottom: marB ? emuToPx(parseInt(marB, 10)) : defaultPadPx,
        };

        // 수직 정렬 anchor: t(top) / ctr(center) / b(bottom)
        const anchor = attr(tcPr, 'anchor');
        if (anchor) cell.anchor = anchor;

        const borders: PptxTableCell['borders'] = {};
        const sides = ['lnL', 'lnR', 'lnT', 'lnB'] as const;
        const sideMap = { lnL: 'left', lnR: 'right', lnT: 'top', lnB: 'bottom' } as const;
        for (const side of sides) {
          const lnEl = getEl(tcPr, A_NS, side);
          if (lnEl) {
            // noFill → 테두리 없음 처리
            const noFill = getEl(lnEl, A_NS, 'noFill');
            if (noFill) continue;
            const border: PptxBorder = {};
            const w = attr(lnEl, 'w');
            if (w) border.width = emuToPx(parseInt(w, 10)) || 1;
            const sf = directChildrenNS(lnEl, A_NS, 'solidFill')[0];
            if (sf) {
              const borderColor = parseColor(sf, theme);
              // alpha=0 → 투명 → 테두리 없음
              if (borderColor === 'transparent') continue;
              border.color = borderColor;
            }
            borders[sideMap[side]] = border;
          }
        }
        if (Object.keys(borders).length > 0) cell.borders = borders;
      }

      const txBody = getEl(tcEl, A_NS, 'txBody');
      if (txBody) cell.textBody = parseTextBody(txBody, theme);

      row.cells.push(cell);
    }

    rows.push(row);
  }

  return { rows, gridCols, styleRef };
}

/* ------------------------------------------------------------------ */
/* ShapeTree 파싱                                                       */
/* ------------------------------------------------------------------ */

/** 트리 전체의 모든 p:cNvPr@id 최대값(숫자만). 편집기 신규 도형 id 유일성 발급용. */
function maxCNvIdInTree(root: Element): number {
  let max = 0;
  const cNvPrs = root.getElementsByTagNameNS(P_NS, 'cNvPr');
  for (let i = 0; i < cNvPrs.length; i++) {
    const raw = cNvPrs[i].getAttribute('id');
    if (raw && /^\d+$/.test(raw)) {
      const n = parseInt(raw, 10);
      if (n > max) max = n;
    }
  }
  return max;
}

function parseShapeTree(parentEl: Element, theme: PptxTheme): PptxShape[] {
  const shapes: PptxShape[] = [];

  for (let i = 0; i < parentEl.childNodes.length; i++) {
    const child = parentEl.childNodes[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;

    if (el.localName === 'sp' && el.namespaceURI === P_NS) {
      const shape = parseShape(el, theme);
      if (shape) shapes.push(shape);
    } else if (el.localName === 'pic' && el.namespaceURI === P_NS) {
      const pic = parsePicture(el, theme);
      if (pic) shapes.push(pic);
    } else if (el.localName === 'grpSp' && el.namespaceURI === P_NS) {
      const grp = parseGroupShape(el, theme);
      if (grp) shapes.push(grp);
    } else if (el.localName === 'graphicFrame' && el.namespaceURI === P_NS) {
      const gf = parseGraphicFrame(el, theme);
      if (gf) shapes.push(gf);
    } else if (el.localName === 'cxnSp' && el.namespaceURI === P_NS) {
      const conn = parseShape(el, theme);
      if (conn) shapes.push(conn);
    }
  }

  return shapes;
}

/* ------------------------------------------------------------------ */
/* Background 파싱                                                      */
/* ------------------------------------------------------------------ */

function parseBackground(bgEl: Element | null, theme: PptxTheme): PptxBackground | undefined {
  if (!bgEl) return undefined;

  // bgPr contains an explicit fill
  const bgPr = getEl(bgEl, P_NS, 'bgPr');
  if (bgPr) {
    const fill = parseFill(bgPr, theme);
    return fill ? { fill } : undefined;
  }

  // bgRef references a theme fill style by index
  const bgRef = getEl(bgEl, P_NS, 'bgRef');
  if (bgRef) {
    // fmtScheme(bgFillStyleLst/fillStyleLst) 해석 — phClr 는 bgRef 의 자식 색.
    // idx=1001 solid phClr 는 기존(참조 색 solid)과 동등, 1002~ 는 그라디언트가 살아난다.
    const fill = resolveStyleRefFill(bgRef, theme);
    if (fill && fill.type !== 'none') return { fill };
    // fmtScheme 부재 + 참조 색 없음 — 기존 휴리스틱 유지 (idx>=1000 → lt1 근사)
    const idx = attr(bgRef, 'idx');
    if (idx) {
      const idxNum = parseInt(idx, 10);
      if (idxNum >= 1000) {
        return { fill: { type: 'solid', color: theme.colors.lt1 } };
      }
    }
  }

  return undefined;
}

/* ------------------------------------------------------------------ */
/* 메인 파서                                                             */
/* ------------------------------------------------------------------ */

export async function parsePptx(buffer: ArrayBuffer): Promise<PptxPresentation> {
  _idCounter = 0;
  const zip = await JSZip.loadAsync(buffer);
  const rawParts = new Map<string, string>();

  const presXml = await readPart(zip, 'ppt/presentation.xml');
  if (!presXml) throw new Error('ppt/presentation.xml을 찾을 수 없습니다.');
  rawParts.set('ppt/presentation.xml', presXml);
  const presDom = new DOMParser().parseFromString(presXml, 'application/xml');

  const presRelsXml = await readPart(zip, 'ppt/_rels/presentation.xml.rels');
  const presRels = presRelsXml ? parseRels(presRelsXml) : new Map<string, string>();

  const themeXml = await findAndReadTheme(zip, presRels);
  const theme = themeXml
    ? parseTheme(themeXml)
    : {
        ...DEFAULT_THEME,
        colors: { ...DEFAULT_THEME.colors },
        fonts: { ...DEFAULT_THEME.fonts },
        colorMap: { ...DEFAULT_THEME.colorMap },
        lineStyles: DEFAULT_THEME.lineStyles.map((line) => ({ ...line })),
      };

  const slideSizeEl = getEl(presDom.documentElement, P_NS, 'sldSz');
  const slideSize = {
    width: slideSizeEl ? emuToPx(parseInt(attr(slideSizeEl, 'cx') || '9144000', 10)) : emuToPx(9144000),
    height: slideSizeEl ? emuToPx(parseInt(attr(slideSizeEl, 'cy') || '6858000', 10)) : emuToPx(6858000),
  };

  const sldIdLstEl = getEl(presDom.documentElement, P_NS, 'sldIdLst');
  const slideRIds: string[] = [];
  if (sldIdLstEl) {
    const sldIdEls = directChildrenNS(sldIdLstEl, P_NS, 'sldId');
    for (const sldIdEl of sldIdEls) {
      const rId = sldIdEl.getAttributeNS(R_NS, 'id') || attr(sldIdEl, 'r:id');
      if (rId) slideRIds.push(rId);
    }
  }

  const slidePartPaths: string[] = [];
  for (const rId of slideRIds) {
    const target = presRels.get(rId);
    if (target) {
      const path = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
      slidePartPaths.push(path);
    }
  }
  const sldIdLstParts = [...slidePartPaths]; // 파스 시점 기준선(패처 고스트 판별용)

  // 파트별 rels 수집 — 슬라이드뿐 아니라 레이아웃/마스터의 미디어(rId)도
  // 배경 blipFill 해석에 필요하므로 한 맵에 모은다 (media 키: `partPath::rId`)
  const allPartRels = new Map<string, Map<string, string>>();

  const slideMasters = await parseSlideMasters(zip, presRels, theme, rawParts, allPartRels);
  const slideLayouts = await parseSlideLayouts(zip, presRels, slideMasters, theme, rawParts, allPartRels);

  const slides: PptxSlide[] = [];

  for (let idx = 0; idx < slidePartPaths.length; idx++) {
    const partPath = slidePartPaths[idx];
    const slideXml = await readPart(zip, partPath);
    if (!slideXml) continue;
    rawParts.set(partPath, slideXml);

    const dir = partPath.substring(0, partPath.lastIndexOf('/'));
    const fileName = partPath.substring(partPath.lastIndexOf('/') + 1);
    const slideRelsPath = `${dir}/_rels/${fileName}.rels`;
    const slideRelsXml = await readPart(zip, slideRelsPath);
    const slideRels = slideRelsXml ? parseRels(slideRelsXml) : new Map<string, string>();
    allPartRels.set(partPath, slideRels);

    let layoutRef: string | undefined;
    for (const [, target] of slideRels) {
      if (target.includes('slideLayout')) {
        layoutRef = resolvePath(dir, target);
        break;
      }
    }

    const layout = layoutRef
      ? slideLayouts.find((candidate) => candidate.partPath === layoutRef)
      : undefined;

    const slideDom = new DOMParser().parseFromString(slideXml, 'application/xml');
    const baseColorMap = layout?.colorMap || theme.colorMap;
    const colorOverride = parseColorMapOverride(slideDom.documentElement);
    const colorMap = colorOverride === 'master'
      ? baseColorMap
      : mergeColorMap(baseColorMap, colorOverride);
    const slideTheme = withColorMap(theme, colorMap);

    const cSld = getEl(slideDom.documentElement, P_NS, 'cSld');
    if (!cSld) continue;

    const spTree = getEl(cSld, P_NS, 'spTree');
    const shapes = spTree ? parseShapeTree(spTree, slideTheme) : [];
    // 파트 전체 cNvPr@id 최대값 — 편집기의 신규 도형 id 발급 시 파서가 모델에
    // 안 담은 요소(SmartArt/OLE graphicFrame·xfrm없는 sp·root nvGrpSpPr id=1 등)
    // 와의 충돌을 막는다(모델 순회만으로는 그 id 들을 못 봄).
    const maxCNvId = spTree ? maxCNvIdInTree(spTree) : 0;

    const bgEl = getEl(cSld, P_NS, 'bg');
    const background = parseBackground(bgEl, slideTheme);

    slides.push({
      id: `slide_${idx + 1}`,
      index: idx,
      partPath,
      shapes,
      background,
      layoutRef,
      colorMap,
      maxCNvId,
      // p:sld/@show="0" = 슬라이드 쇼 건너뛰기(숨김)
      hidden: attr(slideDom.documentElement, 'show') === '0' || undefined,
    });
  }

  const media = await loadMedia(zip, allPartRels);

  // 그림 채움(picture fill)의 rId → data URL 해석 — 파트별 rels 가 다르므로
  // 각 파트(슬라이드/레이아웃/마스터)의 partPath 로 media 맵을 조회해 주입
  for (const slide of slides) {
    resolvePictureFillUrls(slide.shapes, slide.background, slide.partPath, media);
  }
  for (const layout of slideLayouts) {
    resolvePictureFillUrls(layout.shapes, layout.background, layout.partPath, media);
  }
  for (const master of slideMasters) {
    resolvePictureFillUrls(master.shapes, master.background, master.partPath, media);
  }

  // Resolve placeholder transforms from layouts/masters
  for (const slide of slides) {
    resolvePlaceholderTransforms(slide, slideLayouts, slideMasters);
  }

  // 차트 파트 해석 — graphicFrame 이 기록한 chartRId 를 슬라이드 rels 로
  // 차트 파트 경로로 바꾸고, 파트 XML 을 파싱해 shape.chart 를 채운다.
  for (const slide of slides) {
    const rels = allPartRels.get(slide.partPath);
    if (!rels) continue;
    const dir = slide.partPath.substring(0, slide.partPath.lastIndexOf('/'));
    const resolveChart = async (shape: PptxShape): Promise<void> => {
      if (shape.chartRId) {
        const target = rels.get(shape.chartRId);
        if (target) {
          const chartPath = target.startsWith('/')
            ? target.slice(1)
            : normalizePartPath(`${dir}/${target}`);
          const chartXml = await readPart(zip, chartPath);
          if (chartXml) {
            try {
              shape.chart = parseChartPart(
                chartXml,
                (el) => parseColor(el, theme),
                // spPr 채움(그라데이션 포함) 어댑터 — parseFill 재사용
                (parentEl) => {
                  const f = parseFill(parentEl, theme);
                  if (!f) return undefined;
                  if (f.type === 'solid') return { color: f.color };
                  if (f.type === 'gradient' && f.gradientStops) {
                    return {
                      gradient: { stops: f.gradientStops, angle: f.gradientAngle },
                    };
                  }
                  return undefined;
                },
              ) ?? undefined;
            } catch {
              // 파싱 실패 — 렌더러가 자리표시자 유지
            }
          }
        }
      }
      for (const child of shape.children ?? []) await resolveChart(child);
    };
    for (const shape of slide.shapes) await resolveChart(shape);
  }

  // 표 스타일(ppt/tableStyles.xml) 적용 — tblPr 의 tableStyleId + 플래그를
  // 셀 fill/테두리/헤더 bold 로 굽는다(명시 tcPr 이 항상 우선).
  const tableStylesXml = await readPart(zip, 'ppt/tableStyles.xml');
  if (tableStylesXml) {
    const tableStyles = parseTableStyles(tableStylesXml, theme);
    if (tableStyles.size > 0) {
      const applyToShape = (shape: PptxShape): void => {
        if (shape.tableGrid?.styleRef?.styleId) {
          const style = tableStyles.get(shape.tableGrid.styleRef.styleId);
          if (style) applyTableStyleToGrid(shape.tableGrid, style);
        }
        shape.children?.forEach(applyToShape);
      };
      for (const slide of slides) {
        slide.shapes.forEach(applyToShape);
      }
    }
  }

  // 슬라이드 번호 필드(a:fld type="slidenum") 치환 — 캐시 텍스트('‹#›' 등)
  // 대신 실제 1-based 슬라이드 번호를 넣는다. 라운드트립 안전: 패처는
  // type==='text' 런만 XML 에 되쓰므로 field 런의 치환 텍스트는 저장 경로에
  // 영향을 주지 않는다.
  slides.forEach((slide, slideIdx) => {
    for (const shape of slide.shapes) {
      resolveSlideNumberFields(shape, slideIdx + 1);
    }
  });

  // 전체 슬라이드에서 실제 사용된 폰트 수집
  const usedFontSet = new Set<string>();
  for (const slide of slides) {
    for (const shape of slide.shapes) {
      collectShapeFonts(shape, usedFontSet);
    }
  }

  return {
    slides,
    slideSize,
    theme,
    slideMasters,
    slideLayouts,
    media,
    rawParts,
    zip,
    usedFonts: Array.from(usedFontSet),
    sldIdLstParts,
    slideArchiveSnapshots: new Map(),
  };
}

/* ------------------------------------------------------------------ */
/* 표 스타일 (ppt/tableStyles.xml)                                       */
/* ------------------------------------------------------------------ */

const TABLE_STYLE_PARTS = [
  'wholeTbl', 'band1H', 'band2H', 'firstRow', 'lastRow', 'firstCol', 'lastCol',
] as const;

/** ppt/tableStyles.xml → styleId 별 파트 스타일 맵 */
function parseTableStyles(xml: string, theme: PptxTheme): Map<string, PptxTableStyle> {
  const styles = new Map<string, PptxTableStyle>();
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const styleEls = doc.getElementsByTagNameNS(A_NS, 'tblStyle');

  const parseBorder = (lnEl: Element): PptxBorder | undefined => {
    if (getEl(lnEl, A_NS, 'noFill')) return undefined;
    const border: PptxBorder = {};
    const w = attr(lnEl, 'w');
    if (w) border.width = emuToPx(parseInt(w, 10)) || 1;
    const sf = getEl(lnEl, A_NS, 'solidFill');
    if (sf) {
      const c = parseColor(sf, theme);
      if (c === 'transparent') return undefined;
      border.color = c;
    }
    return border;
  };

  const parsePart = (partEl: Element): PptxTableStylePart | undefined => {
    const part: PptxTableStylePart = {};
    const tcStyle = getEl(partEl, A_NS, 'tcStyle');
    if (tcStyle) {
      const fillEl = getEl(tcStyle, A_NS, 'fill');
      if (fillEl) {
        const sf = getEl(fillEl, A_NS, 'solidFill');
        if (sf) {
          const c = parseColor(sf, theme);
          if (c && c !== 'transparent') part.fillColor = c;
        }
      }
      const tcBdr = getEl(tcStyle, A_NS, 'tcBdr');
      if (tcBdr) {
        const borders: NonNullable<PptxTableStylePart['borders']> = {};
        for (const side of ['left', 'right', 'top', 'bottom', 'insideH', 'insideV'] as const) {
          const sideEl = getEl(tcBdr, A_NS, side);
          const lnEl = sideEl ? getEl(sideEl, A_NS, 'ln') : null;
          if (lnEl) {
            const b = parseBorder(lnEl);
            if (b) borders[side] = b;
          }
        }
        if (Object.keys(borders).length > 0) part.borders = borders;
      }
    }
    const tcTx = getEl(partEl, A_NS, 'tcTxStyle');
    if (tcTx && attr(tcTx, 'b') === 'on') part.bold = true;
    return Object.keys(part).length > 0 ? part : undefined;
  };

  for (let i = 0; i < styleEls.length; i++) {
    const styleEl = styleEls[i];
    const styleId = attr(styleEl, 'styleId');
    if (!styleId) continue;
    const style: PptxTableStyle = {};
    for (const partName of TABLE_STYLE_PARTS) {
      const partEl = getEl(styleEl, A_NS, partName);
      if (partEl) {
        const part = parsePart(partEl);
        if (part) style[partName] = part;
      }
    }
    if (Object.keys(style).length > 0) styles.set(styleId, style);
  }
  return styles;
}

/**
 * 표 스타일을 셀에 굽는다 — wholeTbl 기반(outer/inside 테두리 영역 의미 해석)
 * 위에 밴딩(band1H/2H)·firstRow/lastRow/firstCol/lastCol 을 순서대로 덮고,
 * 셀의 명시 tcPr(fill/테두리)가 항상 이긴다. PowerPoint 의 표 스타일 상속을
 * 근사한다(tblStyleLst 미해석 시 표가 테두리·밴딩 없이 렌더되던 갭).
 */
function applyTableStyleToGrid(grid: PptxTableGrid, style: PptxTableStyle): void {
  const flags = grid.styleRef ?? {};
  const rowCount = grid.rows.length;
  const colCount = grid.gridCols.length;

  grid.rows.forEach((row, r) => {
    row.cells.forEach((cell, c) => {
      const isFirstRow = !!flags.firstRow && r === 0;
      const isLastRow = !!flags.lastRow && r === rowCount - 1;
      const isFirstCol = !!flags.firstCol && c === 0;
      const isLastCol = !!flags.lastCol && c === colCount - 1;

      // 밴딩 — 헤더 행 제외한 본문 행 기준 홀짝
      let bandPart: PptxTableStylePart | undefined;
      if (flags.bandRow && !isFirstRow && !isLastRow) {
        const bodyIdx = r - (flags.firstRow ? 1 : 0);
        bandPart = bodyIdx % 2 === 0 ? style.band1H : style.band2H;
      }

      // fill — 나중 파트가 이김, 셀 명시 fill 최우선
      const fillColor =
        (isFirstRow ? style.firstRow?.fillColor : undefined)
        ?? (isLastRow ? style.lastRow?.fillColor : undefined)
        ?? (isFirstCol ? style.firstCol?.fillColor : undefined)
        ?? (isLastCol ? style.lastCol?.fillColor : undefined)
        ?? bandPart?.fillColor
        ?? style.wholeTbl?.fillColor;
      if (fillColor && (!cell.fill || cell.fill.type === undefined)) {
        cell.fill = { type: 'solid', color: fillColor };
      }

      // 테두리 — wholeTbl 의 영역 의미(outer 4방향 + inside)를 셀 방향으로 환산
      const whole = style.wholeTbl?.borders;
      const styleBorders: NonNullable<PptxTableCell['borders']> = {};
      if (whole) {
        const top = r === 0 ? whole.top : whole.insideH;
        const bottom = r === rowCount - 1 ? whole.bottom : whole.insideH;
        const left = c === 0 ? whole.left : whole.insideV;
        const right = c === colCount - 1 ? whole.right : whole.insideV;
        if (top) styleBorders.top = top;
        if (bottom) styleBorders.bottom = bottom;
        if (left) styleBorders.left = left;
        if (right) styleBorders.right = right;
      }
      // firstRow/lastRow 파트의 top/bottom 은 해당 행에 덮어쓴다
      if (isFirstRow && style.firstRow?.borders) {
        const fb = style.firstRow.borders;
        if (fb.top) styleBorders.top = fb.top;
        if (fb.bottom) styleBorders.bottom = fb.bottom;
      }
      if (isLastRow && style.lastRow?.borders) {
        const lb = style.lastRow.borders;
        if (lb.top) styleBorders.top = lb.top;
        if (lb.bottom) styleBorders.bottom = lb.bottom;
      }
      if (Object.keys(styleBorders).length > 0) {
        cell.borders = { ...styleBorders, ...cell.borders };
      }

      // firstRow/lastRow bold — 셀 문단 기본 서식에만 주입(런 명시가 이김)
      const bold = (isFirstRow && style.firstRow?.bold)
        || (isLastRow && style.lastRow?.bold)
        || (isFirstCol && style.firstCol?.bold)
        || (isLastCol && style.lastCol?.bold);
      if (bold && cell.textBody) {
        for (const para of cell.textBody.paragraphs) {
          para.defaultRunProps = { bold: true, ...para.defaultRunProps };
        }
      }
    });
  });
}

/**
 * 도형(그룹/표 셀 포함)의 slidenum 필드 런에 실제 슬라이드 번호를 주입한다.
 * datetime 등 다른 필드는 캐시 텍스트를 유지한다(원본 표시값 보존).
 */
function resolveSlideNumberFields(shape: PptxShape, slideNumber: number): void {
  const substitute = (paragraphs: PptxParagraph[] | undefined): void => {
    if (!paragraphs) return;
    for (const para of paragraphs) {
      for (const run of para.runs) {
        if (run.type === 'field' && run.fieldType === 'slidenum') {
          run.text = String(slideNumber);
        }
      }
    }
  };
  substitute(shape.textBody?.paragraphs);
  if (shape.tableGrid) {
    for (const row of shape.tableGrid.rows) {
      for (const cell of row.cells) {
        substitute(cell.textBody?.paragraphs);
      }
    }
  }
  if (shape.children) {
    for (const child of shape.children) {
      resolveSlideNumberFields(child, slideNumber);
    }
  }
}

/** 그림 채움의 imageRId 를 파트별 media 맵에서 data URL 로 해석해 주입 */
function resolveFillImageUrl(
  fill: PptxFill | undefined,
  partPath: string,
  media: Map<string, string>,
): void {
  if (!fill || fill.type !== 'picture' || !fill.imageRId) return;
  const url = media.get(`${partPath}::${fill.imageRId}`);
  if (url) fill.imageDataUrl = url;
}

function resolveShapeFillImages(
  shape: PptxShape,
  partPath: string,
  media: Map<string, string>,
): void {
  resolveFillImageUrl(shape.fill, partPath, media);
  if (shape.tableGrid) {
    for (const row of shape.tableGrid.rows) {
      for (const cell of row.cells) {
        resolveFillImageUrl(cell.fill, partPath, media);
      }
    }
  }
  if (shape.children) {
    for (const child of shape.children) {
      resolveShapeFillImages(child, partPath, media);
    }
  }
}

function resolvePictureFillUrls(
  shapes: PptxShape[],
  background: PptxBackground | undefined,
  partPath: string,
  media: Map<string, string>,
): void {
  resolveFillImageUrl(background?.fill, partPath, media);
  for (const shape of shapes) {
    resolveShapeFillImages(shape, partPath, media);
  }
}

function collectTextBodyFonts(textBody: PptxTextBody, fonts: Set<string>): void {
  for (const para of textBody.paragraphs) {
    for (const run of para.runs) {
      if (run.fontFamily) fonts.add(run.fontFamily);
      if (run.eaFontFamily) fonts.add(run.eaFontFamily);
      if (run.csFontFamily) fonts.add(run.csFontFamily);
    }
    if (para.defaultRunProps?.fontFamily) fonts.add(para.defaultRunProps.fontFamily);
    if (para.defaultRunProps?.eaFontFamily) fonts.add(para.defaultRunProps.eaFontFamily);
    if (para.defaultRunProps?.csFontFamily) fonts.add(para.defaultRunProps.csFontFamily);
    if (para.bulletFontFamily) fonts.add(para.bulletFontFamily);
  }
}

function collectShapeFonts(shape: PptxShape, fonts: Set<string>): void {
  if (shape.textBody) {
    collectTextBodyFonts(shape.textBody, fonts);
  }
  if (shape.type === 'table' && shape.tableGrid) {
    for (const row of shape.tableGrid.rows) {
      for (const cell of row.cells) {
        if (cell.textBody) collectTextBodyFonts(cell.textBody, fonts);
      }
    }
  }
  // 그룹 내부 텍스트의 폰트도 수집 (기존엔 최상위 도형만 순회해 누락)
  if (shape.children) {
    for (const child of shape.children) {
      collectShapeFonts(child, fonts);
    }
  }
}

function resolvePlaceholderTransforms(
  slide: PptxSlide,
  layouts: PptxSlideLayout[],
  masters: PptxSlideMaster[],
): void {
  const layout = slide.layoutRef
    ? layouts.find(l => l.partPath === slide.layoutRef)
    : undefined;
  const master = layout?.masterRef
    ? masters.find((candidate) => candidate.partPath === layout.masterRef)
    : masters[0];

  for (const shape of slide.shapes) {
    if (!shape.placeholder) continue;
    // 상속 체인: 레이아웃 ph → 마스터 ph. 종전에는 첫 매치(대개 레이아웃)에서
    // 멈춰서, 레이아웃에 없는 속성(예: subTitle 색 — 마스터 body ph 의
    // defRPr solidFill)이 유실돼 검정 배경 위 검정 텍스트로 사라지는 사고가
    // 났다(BayesianOpt.pptx 실측). 머지는 전부 ??-기반이라 체인이 안전하다.
    const layoutMatch = findPptxPlaceholderMatch(shape.placeholder, layout?.shapes ?? []);
    const masterMatch = findPptxPlaceholderMatch(shape.placeholder, master?.shapes ?? []);

    // 텍스트 스타일 상속: 레이아웃 ph → 마스터 ph → 마스터 p:txStyles.
    // (ph 매치가 없어도 txStyles 단계는 적용 — 기본 템플릿은 크기/불릿을 여기 정의)
    if (layoutMatch) inheritPlaceholderTextDefaults(shape, layoutMatch);
    if (masterMatch && masterMatch !== layoutMatch) {
      inheritPlaceholderTextDefaults(shape, masterMatch);
    }
    applyMasterTextStyles(shape, master);

    const primary = layoutMatch ?? masterMatch;
    if (!primary) continue;

    // Transform geometry needs its own effective source. A layout placeholder
    // may be the best text/style match while carrying a sentinel 0x0 xfrm; in
    // that case it must not mask usable master geometry further up the chain.
    const effectiveTransform = resolvePptxPlaceholderGeometry(
      shape,
      layout?.shapes,
      master?.shapes,
    );

    // Placeholder-only exception: an explicit xfrm with a zero/non-positive
    // extent is not effective geometry.  Fall back as a whole (position+size),
    // matching the same layout -> master chain used by presentation clients.
    if (
      effectiveTransform
      && (!shape.hasExplicitTransform || !hasUsablePlaceholderGeometry(shape))
    ) {
      shape.size = effectiveTransform.size;
      shape.position = effectiveTransform.position;
    }

    // 도형 지오메트리 상속 — 슬라이드 ph 가 기본 rect(또는 미지정)이면
    // 레이아웃/마스터 ph 의 것을 따른다. 그림 placeholder 가 아치 등
    // custGeom 으로 클리핑되는 템플릿(startup_pitchdeck 실측)에서 필수.
    const hasOwnGeometry = !!shape.geometry
      && !(shape.geometry.preset === 'rect' && !shape.geometry.custom);
    if (!hasOwnGeometry && primary.geometry
      && !(primary.geometry.preset === 'rect' && !primary.geometry.custom)) {
      shape.geometry = primary.geometry;
    }

  }
}

/**
 * placeholder 타입 폴백 — OOXML 상속 규칙상 마스터에 동일 타입 ph 가 없을 때
 * 참조하는 상위 타입 (subTitle/body 는 bodyStyle 계열, ctrTitle 은 title 계열).
 */
const PH_TYPE_FALLBACKS: Record<string, string[]> = {
  subTitle: ['body'],
  ctrTitle: ['title'],
};

/** Both extents must be positive for placeholder geometry to be effective. */
function hasUsablePlaceholderGeometry(shape: PptxShape): boolean {
  return shape.size.width > 0 && shape.size.height > 0;
}

/**
 * Resolve effective placeholder transform geometry with parser matching parity:
 * exact type+idx -> exact type -> type alias, walking sources in order.
 * Non-placeholder shapes and non-positive extents never inherit through this API.
 */
export function resolvePptxPlaceholderGeometry(
  shape: PptxShape,
  ...sources: (PptxShape[] | undefined)[]
): { position: { x: number; y: number }; size: { width: number; height: number } } | null {
  if (hasUsablePlaceholderGeometry(shape)) {
    return { position: { ...shape.position }, size: { ...shape.size } };
  }
  if (!shape.placeholder) return null;
  let target = shape.placeholder;
  for (const source of sources) {
    // Match before checking geometry. An invalid exact layout match must advance
    // the inheritance chain to master; it must not be discarded so that a less
    // specific, but usable, alias from the same layout wins accidentally.
    const inherited = findPptxPlaceholderMatch(target, source ?? []);
    if (!inherited) continue;
    if (hasUsablePlaceholderGeometry(inherited)) {
      return {
        position: { ...inherited.position },
        size: { ...inherited.size },
      };
    }
    if (inherited.placeholder) target = inherited.placeholder;
  }
  return null;
}

function phTypeMatches(phType: string | undefined, sourceType: string | undefined): boolean {
  if (!phType || !sourceType) return phType === sourceType;
  if (phType === sourceType) return true;
  return PH_TYPE_FALLBACKS[phType]?.includes(sourceType) ?? false;
}

/** Canonical placeholder matcher. Deliberately has no idx-only fallback. */
export function findPptxPlaceholderMatch(
  ph: NonNullable<PptxShape['placeholder']>,
  shapes: readonly PptxShape[],
): PptxShape | undefined {
  // 정확 타입+idx → 정확 타입 → 타입 별칭(subTitle→body 등) 순서로 매칭.
  // idx-only는 body/title 콘텐츠를 교차 연결할 수 있으므로 허용하지 않는다.
  return shapes.find((shape) =>
    shape.placeholder?.type === ph.type && shape.placeholder?.idx === ph.idx)
    ?? shapes.find((shape) => shape.placeholder?.type === ph.type)
    ?? shapes.find((shape) => phTypeMatches(ph.type, shape.placeholder?.type));
}

function mergeBodyProps(primary: PptxBodyProps, fallback: PptxBodyProps): PptxBodyProps {
  return {
    anchor: primary.anchor ?? fallback.anchor,
    anchorCtr: primary.anchorCtr ?? fallback.anchorCtr,
    wrap: primary.wrap ?? fallback.wrap,
    horzOverflow: primary.horzOverflow ?? fallback.horzOverflow,
    vertOverflow: primary.vertOverflow ?? fallback.vertOverflow,
    lIns: primary.lIns ?? fallback.lIns,
    tIns: primary.tIns ?? fallback.tIns,
    rIns: primary.rIns ?? fallback.rIns,
    bIns: primary.bIns ?? fallback.bIns,
    autofit: primary.autofit ?? fallback.autofit,
    autofitFontScale: primary.autofitFontScale ?? fallback.autofitFontScale,
    autofitLnSpcReduction: primary.autofitLnSpcReduction ?? fallback.autofitLnSpcReduction,
  };
}

function mergeParagraphFormatting(
  target: PptxParagraph,
  source?: PptxParagraphStyleDefaults | PptxParagraph,
): void {
  if (!source) return;

  target.alignment = target.alignment ?? source.alignment;
  target.bulletType = target.bulletType ?? source.bulletType;
  target.bulletChar = target.bulletChar ?? source.bulletChar;
  target.bulletAutoNumType = target.bulletAutoNumType ?? source.bulletAutoNumType;
  target.bulletFontFamily = target.bulletFontFamily ?? source.bulletFontFamily;
  target.bulletColor = target.bulletColor ?? source.bulletColor;
  target.bulletSizePts = target.bulletSizePts ?? source.bulletSizePts;
  target.bulletSizePct = target.bulletSizePct ?? source.bulletSizePct;

  if (source.spacing) {
    target.spacing = {
      before: target.spacing?.before ?? source.spacing.before,
      after: target.spacing?.after ?? source.spacing.after,
      lineSpacing: target.spacing?.lineSpacing ?? source.spacing.lineSpacing,
    };
  }

  if (source.indent) {
    target.indent = {
      left: target.indent?.left ?? source.indent.left,
      hanging: target.indent?.hanging ?? source.indent.hanging,
      firstLine: target.indent?.firstLine ?? source.indent.firstLine,
    };
  }

  target.defaultRunProps = mergeParagraphDefaults(target.defaultRunProps, source.defaultRunProps);
}

/**
 * 편집기(새 슬라이드 placeholder 합성)용 — 슬라이드 ph 도형에 레이아웃→마스터
 * 텍스트 기본값 상속을 **파스 시점과 동일 체인**으로 적용한다(bodyProps·문단
 * 서식(bullet/정렬/level)·defaultRunProps(크기/색/폰트)). 라이브 모델이 파스
 * 결과와 같은 규칙을 타야 타이핑 즉시 제목 크기·buNone 등이 정확하다.
 */
export function applyPlaceholderTextInheritance(
  shape: PptxShape,
  layout: PptxSlideLayout | undefined,
  master: PptxSlideMaster | undefined,
): void {
  if (!shape.placeholder) return;
  const layoutMatch = findPptxPlaceholderMatch(shape.placeholder, layout?.shapes ?? []);
  const masterMatch = findPptxPlaceholderMatch(shape.placeholder, master?.shapes ?? []);
  if (layoutMatch) inheritPlaceholderTextDefaults(shape, layoutMatch);
  if (masterMatch && masterMatch !== layoutMatch) {
    inheritPlaceholderTextDefaults(shape, masterMatch);
  }
  applyMasterTextStyles(shape, master);
}

/** placeholder 타입 → 마스터 p:txStyles 스타일 계열. */
function masterStyleKindFor(phType: string | undefined): 'title' | 'body' | 'other' {
  if (phType === 'title' || phType === 'ctrTitle') return 'title';
  if (phType === 'dt' || phType === 'ftr' || phType === 'sldNum' || phType === 'hdr') return 'other';
  return 'body'; // body/subTitle/obj(타입 부재 콘텐츠) 는 bodyStyle 계열
}

/** 상속 사슬의 마스터 p:txStyles 단계 — 레이아웃/마스터 ph 머지 **후에** 호출
 *  (?? 머지라 이미 정해진 속성은 보존). 기본 템플릿의 제목 44pt·본문 불릿 등. */
function applyMasterTextStyles(shape: PptxShape, master: PptxSlideMaster | undefined): void {
  if (!shape.textBody || !shape.placeholder || !master?.textStyles) return;
  const style = master.textStyles[masterStyleKindFor(shape.placeholder.type)];
  if (!style) return;
  for (const para of shape.textBody.paragraphs) {
    const defs = style[para.level ?? 0];
    if (defs) mergeParagraphFormatting(para, defs);
  }
}

function inheritPlaceholderTextDefaults(target: PptxShape, source: PptxShape): void {
  if (!target.textBody || !source.textBody) return;

  target.textBody.bodyProps = mergeBodyProps(target.textBody.bodyProps, source.textBody.bodyProps);

  const sourceParagraphsByLevel = new Map<number, PptxParagraph>();
  for (const para of source.textBody.paragraphs) {
    const level = para.level ?? 0;
    if (!sourceParagraphsByLevel.has(level)) {
      sourceParagraphsByLevel.set(level, para);
    }
  }

  const fallbackParagraph =
    sourceParagraphsByLevel.get(0)
    || source.textBody.paragraphs[0];
  if (!fallbackParagraph && sourceParagraphsByLevel.size === 0) return;

  for (const para of target.textBody.paragraphs) {
    const byLevel = sourceParagraphsByLevel.get(para.level ?? 0);
    mergeParagraphFormatting(para, byLevel || fallbackParagraph);
  }
}

async function findAndReadTheme(zip: JSZip, presRels: Map<string, string>): Promise<string | null> {
  // 올바른 테마 해석 체인은 슬라이드마스터 rels → 테마다. presentation rels 의
  // "첫 번째 theme" 을 집으면 미사용 테마를 읽을 수 있다 — 실측: 구글 슬라이드
  // export(BayesianOpt.pptx)가 theme1(미사용)+theme2(마스터 참조)를 함께 담아
  // dk2 등 스킴 색이 전부 어긋났다. 첫 마스터의 테마를 우선 사용한다.
  for (const [, target] of presRels) {
    if (!target.includes('slideMaster')) continue;
    const masterPath = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
    const dir = masterPath.substring(0, masterPath.lastIndexOf('/'));
    const fileName = masterPath.substring(masterPath.lastIndexOf('/') + 1);
    const relsXml = await readPart(zip, `${dir}/_rels/${fileName}.rels`);
    if (!relsXml) continue;
    for (const [, relTarget] of parseRels(relsXml)) {
      if (!relTarget.includes('theme')) continue;
      const themePath = relTarget.startsWith('/')
        ? relTarget.slice(1)
        : normalizePartPath(`${dir}/${relTarget}`);
      const xml = await readPart(zip, themePath);
      if (xml) return xml;
    }
  }
  for (const [, target] of presRels) {
    if (target.includes('theme')) {
      const path = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
      const xml = await readPart(zip, path);
      if (xml) return xml;
    }
  }
  return readPart(zip, 'ppt/theme/theme1.xml');
}

/** '../' 세그먼트를 해석해 zip 파트 경로를 정규화한다 (ppt/slideMasters/../theme/x → ppt/theme/x). */
function normalizePartPath(path: string): string {
  const parts: string[] = [];
  for (const seg of path.split('/')) {
    if (seg === '..') parts.pop();
    else if (seg !== '.' && seg !== '') parts.push(seg);
  }
  return parts.join('/');
}

async function parseSlideMasters(
  zip: JSZip,
  presRels: Map<string, string>,
  theme: PptxTheme,
  rawParts: Map<string, string>,
  allPartRels: Map<string, Map<string, string>>,
): Promise<PptxSlideMaster[]> {
  const masters: PptxSlideMaster[] = [];

  for (const [, target] of presRels) {
    if (!target.includes('slideMaster')) continue;
    const path = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
    const xml = await readPart(zip, path);
    if (!xml) continue;
    rawParts.set(path, xml);

    // 마스터 배경 blipFill 의 rId 해석을 위해 rels 를 미디어 로딩 대상에 등록
    const dir = path.substring(0, path.lastIndexOf('/'));
    const fileName = path.substring(path.lastIndexOf('/') + 1);
    const masterRelsXml = await readPart(zip, `${dir}/_rels/${fileName}.rels`);
    if (masterRelsXml) allPartRels.set(path, parseRels(masterRelsXml));

    const dom = new DOMParser().parseFromString(xml, 'application/xml');
    const colorMap = mergeColorMap(
      theme.colorMap,
      parseColorMapEl(getEl(dom.documentElement, P_NS, 'clrMap') || getEl(dom.documentElement, A_NS, 'clrMap')),
    );
    const masterTheme = withColorMap(theme, colorMap);
    const cSld = getEl(dom.documentElement, P_NS, 'cSld');
    const shapes = cSld ? parseShapeTree(getEl(cSld, P_NS, 'spTree') || cSld, masterTheme) : [];
    const bgEl = cSld ? getEl(cSld, P_NS, 'bg') : null;
    const background = parseBackground(bgEl, masterTheme);

    // 마스터의 레이아웃 순서(p:sldLayoutIdLst 문서순) — 새 슬라이드 레이아웃 규칙용.
    const layoutRefs: string[] = [];
    const masterRels = allPartRels.get(path);
    const idLst = getEl(dom.documentElement, P_NS, 'sldLayoutIdLst');
    if (idLst && masterRels) {
      for (const idEl of directChildrenNS(idLst, P_NS, 'sldLayoutId')) {
        const rId = idEl.getAttributeNS(R_NS, 'id') || attr(idEl, 'r:id');
        const target = rId ? masterRels.get(rId) : undefined;
        if (target) layoutRefs.push(resolvePath(dir, target));
      }
    }

    // p:txStyles(titleStyle/bodyStyle/otherStyle) — placeholder 스타일 상속의
    // 마스터 단계. 기본 템플릿은 제목 44pt·본문 불릿을 여기에만 정의한다.
    const txStylesEl = getEl(dom.documentElement, P_NS, 'txStyles');
    let textStyles: PptxSlideMaster['textStyles'];
    if (txStylesEl) {
      const parseStyle = (name: string): Record<number, PptxParagraphStyleDefaults> | undefined => {
        const styleEl = getEl(txStylesEl, P_NS, name);
        if (!styleEl) return undefined;
        const out: Record<number, PptxParagraphStyleDefaults> = {};
        for (let lvl = 1; lvl <= 9; lvl++) {
          const lvlPPr = getEl(styleEl, A_NS, `lvl${lvl}pPr`);
          if (lvlPPr) out[lvl - 1] = parseParagraphStyleDefaultsFromPPr(lvlPPr, masterTheme);
        }
        return Object.keys(out).length > 0 ? out : undefined;
      };
      textStyles = {
        title: parseStyle('titleStyle'),
        body: parseStyle('bodyStyle'),
        other: parseStyle('otherStyle'),
      };
    }

    masters.push({ partPath: path, background, shapes, colorMap, layoutRefs, textStyles });
  }

  return masters;
}

async function parseSlideLayouts(
  zip: JSZip,
  presRels: Map<string, string>,
  masters: PptxSlideMaster[],
  theme: PptxTheme,
  rawParts: Map<string, string>,
  allPartRels: Map<string, Map<string, string>>,
): Promise<PptxSlideLayout[]> {
  const layouts: PptxSlideLayout[] = [];
  const layoutPaths = new Set<string>();

  zip.forEach((relativePath: string) => {
    if (relativePath.includes('slideLayouts/') && relativePath.endsWith('.xml') && !relativePath.includes('_rels')) {
      layoutPaths.add(relativePath);
    }
  });

  for (const path of layoutPaths) {
    const xml = await readPart(zip, path);
    if (!xml) continue;
    rawParts.set(path, xml);

    const dir = path.substring(0, path.lastIndexOf('/'));
    const fileName = path.substring(path.lastIndexOf('/') + 1);
    const layoutRelsPath = `${dir}/_rels/${fileName}.rels`;
    const layoutRelsXml = await readPart(zip, layoutRelsPath);
    const layoutRels = layoutRelsXml ? parseRels(layoutRelsXml) : new Map<string, string>();
    // 레이아웃 배경 blipFill 의 rId 해석을 위해 미디어 로딩 대상에 등록
    allPartRels.set(path, layoutRels);

    const dom = new DOMParser().parseFromString(xml, 'application/xml');
    let masterRef: string | undefined;
    for (const [, target] of layoutRels) {
      if (target.includes('slideMaster')) {
        masterRef = resolvePath(dir, target);
        break;
      }
    }

    const baseColorMap = masterRef
      ? (masters.find((master) => master.partPath === masterRef)?.colorMap || theme.colorMap)
      : theme.colorMap;
    const colorOverride = parseColorMapOverride(dom.documentElement);
    const colorMap = colorOverride === 'master'
      ? baseColorMap
      : mergeColorMap(baseColorMap, colorOverride);
    const layoutTheme = withColorMap(theme, colorMap);
    const cSld = getEl(dom.documentElement, P_NS, 'cSld');
    const shapes = cSld ? parseShapeTree(getEl(cSld, P_NS, 'spTree') || cSld, layoutTheme) : [];
    const bgEl = cSld ? getEl(cSld, P_NS, 'bg') : null;
    const background = parseBackground(bgEl, layoutTheme);

    layouts.push({
      partPath: path, background, shapes, masterRef, colorMap,
      type: attr(dom.documentElement, 'type') || undefined,
      name: (cSld && attr(cSld, 'name')) || undefined,
    });
  }

  return layouts;
}

/* ------------------------------------------------------------------ */
/* 미디어 해석 헬퍼 (슬라이드별 rId → data URL)                            */
/* ------------------------------------------------------------------ */

export function resolveMediaUrl(
  slidePartPath: string,
  rId: string,
  media: Map<string, string>,
): string | undefined {
  const key = `${slidePartPath}::${rId}`;
  return media.get(key);
}
